import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signInWithCustomToken, signOut } from 'firebase/auth';
import { collection, addDoc, onSnapshot, doc, updateDoc, deleteDoc, serverTimestamp, setDoc, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getMessaging, getToken } from 'firebase/messaging';
import { Clock, User, Shield, AlertTriangle, CheckCircle, LogOut, Plus, Trash2, Key, Download, Printer, Save, X, Edit2, Moon, Sun, Mail, Calendar, UserCog, Info, Menu, RefreshCw, Activity, FileText, ChevronLeft, ChevronRight, ChevronDown, MoreVertical, CalendarDays, LayoutDashboard, Users, List, History, UserX, UserCheck } from 'lucide-react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PrintableReport from './PrintableReport';
import ShiftScheduleView from './ShiftScheduleView';
import ShiftBoard from './components/features/ShiftBoard';
import { ImpressumModal, DatenschutzModal } from './LegalModals';
import Button from './components/Button';
import { calculateAbsenceHours } from './utils/absenceCalculator';
import TargetHoursModal from './components/modals/TargetHoursModal';
import { app, auth, db, functions, appId, LOGO_URL } from './services/firebase';


// --- KONFIGURATION ---
// Firebase (inkl. App Check) wird zentral in services/firebase.js initialisiert.
const messaging = getMessaging(app);

// --- ÜBERSETZUNGEN ---
const translations = {
  de: {
    loginTitle: 'Anmeldung', nameLabel: 'Benutzername', firstNameLabel: 'Vorname', lastNameLabel: 'Nachname', usernameLabel: 'Benutzername', passwordLabel: 'PIN-Code (6 Zahlen)', emailLabel: 'E-Mail (für Berichte)', loginButton: 'Anmelden', registerButton: 'Registrieren', toggleRegister: 'Konto erstellen', toggleLogin: 'Zum Login', employeeDashboard: 'Mitarbeiter Dashboard', adminConsole: 'Admin Konsole', loggedInAs: 'Angemeldet als', monthlyStatus: 'Monatsstatus', limitReached: 'Limit fast erreicht!', limitExceeded: 'Limit überschritten!', allGood: 'Alles im grünen Bereich', recordTime: 'Zeit erfassen', recordTimeDesc: 'Erfasse deine Arbeitszeiten und Pausen.', dateLabel: 'Datum', startLabel: 'Start', endLabel: 'Ende', saveButton: 'Speichern', history: 'Verlauf', employeeOverview: 'Mitarbeiter', entriesAll: 'Einträge', print: 'Drucken', summary: 'Zusammenfassung', total: 'GESAMT', printTitle: 'Stundenabrechnung', stand: 'Stand', employee: 'Mitarbeiter', time: 'Zeit', duration: 'Dauer', newPasswordTitle: 'Neues Passwort', newPasswordPlaceholder: 'Geheim...', cancel: 'Abbrechen', editEntry: 'Bearbeiten', confirmDelete: 'Löschen?', passChanged: 'Geändert!', saveError: 'Fehler!', fillData: 'Daten fehlen.', userNotFound: 'Unbekannt.', wrongPass: 'Falsches PW.', nameTaken: 'Name vergeben.', usernameTaken: 'Benutzername vergeben.', regError: 'Fehler.', printError: 'Druckfehler.', max: 'Max', current: 'Aktuell', delete: 'Löschen', edit: 'Bearbeiten', logout: 'Logout', sendReport: 'Bericht senden', reportSubject: 'Arbeitszeit', reportBody: 'Hallo', reportBody2: 'hier ist deine aktuelle Übersicht für diesen Monat.', close: 'Schließen', printReport: 'PDF / Drucken',
    securityUpdateTitle: 'Sicherheits-Update erforderlich', securityUpdateMsg: 'Aus Sicherheitsgründen musst du jetzt einen neuen 6-stelligen PIN-Code wählen. Dein bisheriger PIN funktioniert danach nicht mehr.', saveAndLogin: 'Speichern & Anmelden',
    pendingApprovalMsg: 'Registrierung erfolgreich. Bitte warten Sie auf die Freigabe durch einen Administrator.', accountNotApproved: 'Dein Account wurde noch nicht freigegeben.', pendingApprovals: 'Ausstehende Freigaben', approve: 'Freigeben', reject: 'Ablehnen',
    coverageTitle: 'Übersicht', coverageDate: 'Datum wählen', coverageGap: 'Lücke!', coverageStaff: 'MA',
    viewDay: 'Tag', viewWeek: 'Woche', viewMonth: 'Monat', viewAll: 'Alle',
    mo: 'Mo', tu: 'Di', we: 'Mi', th: 'Do', fr: 'Fr', sa: 'Sa', su: 'So',
    overlapError: 'Fehler: Zeit überschneidet sich mit einem existierenden Eintrag!',
    makeAdmin: 'Rechte ändern', adminBadge: 'ADMIN',
    toastSaved: 'Erfolgreich gespeichert!', toastDeleted: 'Eintrag gelöscht.', toastRoleChanged: 'Rolle aktualisiert!', toastWelcome: 'Willkommen!', toastNoMail: 'Keine E-Mail hinterlegt.',
    refresh: 'Aktualisieren',
    settingsDesign: 'Design', settingsLang: 'Sprache', settingsPass: 'PIN ändern',
    coverageLegend: 'Personalstärke:',
    tabSchedule: 'Schichtplan', tabGraph: 'Trend (Graph)', staffCount: 'Anzahl MA',
    pinError: 'PIN muss aus 6 Zahlen bestehen.',
    selectMonth: 'Monat wählen',
    monthDone: 'Alles erledigt diesen Monat!',
    monthOver: 'Du brauchst eine Pause diesen Monat!',
    confirmDeleteEmployee: 'Mitarbeiter löschen?',
    confirmDeleteEmployeeText: 'Möchten Sie diesen Mitarbeiter wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    employeeDeleted: 'Mitarbeiter gelöscht.',
    cannotDeleteSelf: 'Sie können sich nicht selbst löschen.',
    deactivateEmployee: 'Mitarbeiter deaktivieren',
    reactivateEmployee: 'Reaktivieren',
    deactivatedBadge: 'INAKTIV',
    deactivatedFrom: 'Deaktiviert ab',
    deactivatedSince: 'Inaktiv seit',
    deactivateFromDate: 'Ab welchem Datum soll der Mitarbeiter gesperrt werden?',
    deactivateWarning: 'Ab diesem Datum kann der Mitarbeiter sich nicht mehr einloggen und erscheint nicht mehr in der Coverage-Ansicht. Alle historischen Daten bleiben erhalten.',
    employeeDeactivated: 'Mitarbeiter deaktiviert ab',
    employeeReactivated: 'Mitarbeiter reaktiviert.',
    deactivatedEmployees: 'Deaktivierte Mitarbeiter',
    accountDisabled: 'Dieser Account wurde deaktiviert. Bitte wenden Sie sich an einen Administrator.',
    confirmDeactivate: 'Deaktivierung bestätigen',
    // Shift Scheduling
    scheduleTab: 'Schichtplan',
    myShiftsTab: 'Meine Schichten',
    addShift: 'Schicht hinzufügen',
    editShift: 'Schicht bearbeiten',
    deleteShift: 'Schicht löschen',
    shiftType: 'Schichttyp',
    shiftNotes: 'Notizen',
    selectEmployee: 'Mitarbeiter wählen',
    shiftConflict: 'Konflikt: Mitarbeiter hat bereits eine Schicht zu dieser Zeit',
    noShiftsScheduled: 'Keine Schichten geplant',
    upcomingShifts: 'Kommende Schichten',
    today: 'Heute',
    dayOff: 'Frei',
    week: 'Woche',
    previousWeek: 'Vorherige Woche',
    nextWeek: 'Nächste Woche',
    monday: 'Montag',
    tuesday: 'Dienstag',
    wednesday: 'Mittwoch',
    thursday: 'Donnerstag',
    friday: 'Freitag',
    saturday: 'Samstag',
    sunday: 'Sonntag',
    signatureEmployee: 'Mitarbeiter',
    signatureEmployer: 'Arbeitgeber',
    lastLogin: 'Letzter Login',
    lastTimeEntry: 'Letzter Eintrag',
    never: 'Nie',
    addEntry: 'Eintrag hinzufügen',
    breakLabel: 'Pause (Min)',
    breakStartLabel: 'Pause Start',
    breakEndLabel: 'Pause Ende',
    breakWarning6h: 'Achtung: Bei über 6 Stunden Arbeit sind gesetzlich 30 Minuten Pause vorgeschrieben. Trotzdem speichern?',
    breakWarning9h: 'Achtung: Bei über 9 Stunden Arbeit sind gesetzlich 45 Minuten Pause vorgeschrieben. Trotzdem speichern?',
    negativeDuration: 'Fehler: Arbeitszeit minus Pause ist negativ oder Null.',
    addBreak: 'Pause hinzufügen',
    breaks: 'Pausen',
    allEmployees: 'Alle Mitarbeiter',
    // New Keys
    type: 'Typ',
    work: 'Arbeit',
    vacation: 'Urlaub',
    sick: 'Krankheit',
    fullDay: 'Ganzer Tag',
    halfDay: 'Halber Tag',
    dateFrom: 'Von',
    dateTo: 'Bis',
    futureWorkError: 'Arbeitszeit kann nicht in der Zukunft liegen.',
  },
  el: {
    loginTitle: 'Σύνδεση', nameLabel: 'Όνομα', firstNameLabel: 'Όνομα', lastNameLabel: 'Επίθετο', passwordLabel: 'PIN (6 ψηφία)', emailLabel: 'Email', loginButton: 'Σύνδεση', registerButton: 'Εγγραφή', toggleRegister: 'Εγγραφή', toggleLogin: 'Σύνδεση', employeeDashboard: 'Ταμπλό', adminConsole: 'Κονσόλα', loggedInAs: 'Χρήστης', monthlyStatus: 'Κατάσταση', limitReached: 'Όριο κοντά!', limitExceeded: 'Όριο!', allGood: 'Όλα καλά', recordTime: 'Καταγραφή', dateLabel: 'Ημερομηνία', startLabel: 'Έναρξη', endLabel: 'Λήξη', saveButton: 'Αποθήκευση', history: 'Ιστορικό', employeeOverview: 'Υπάλληλοι', entriesAll: 'Καταχωρήσεις', print: 'Εκτύπωση', summary: 'Σύνοψη', total: 'ΣΥΝΟΛΟ', printTitle: 'Αναφορά', stand: 'Ημ/νία', employee: 'Υπάλληλος', time: 'Ώρα', duration: 'Διάρκεια', newPasswordTitle: 'Νέο PIN', newPasswordPlaceholder: '123456', cancel: 'Ακύρωση', editEntry: 'Επεξεργασία', confirmDelete: 'Διαγραφή;', passChanged: 'Το PIN άλλαξε!', saveError: 'Σφάλμα.', fillData: 'Ελλιπή στοιχεία.', userNotFound: 'Άγνωστος.', wrongPass: 'Λάθος PIN.', nameTaken: 'Υπάρχει.', regError: 'Σφάλμα.', printError: 'Σφάλμα.', max: 'Μέγ.', current: 'Τρέχον', delete: 'Διαγραφή', edit: 'Επεξεργασία', logout: 'Έξοδος', sendReport: 'Αποστολή', reportSubject: 'Αναφορά', reportBody: 'Γεια', reportBody2: 'εδώ είναι η αναφορά σου.', close: 'Κλείσιμο', printReport: 'Εκτύπωση',
    coverageTitle: 'Επισκόπηση', coverageDate: 'Ημερομηνία', coverageGap: 'Κενό!', coverageStaff: 'Πρσ',
    viewDay: 'Ημέρα', viewWeek: 'Εβδομάδα', viewMonth: 'Μήνας', viewAll: 'Όλα',
    mo: 'Δε', tu: 'Τρ', we: 'Τε', th: 'Πε', fr: 'Πα', sa: 'Σα', su: 'Κυ',
    overlapError: 'Σφάλμα: Η ώρα συμπίπτει με υπάρχουσα εγγραφή!',
    makeAdmin: 'Αλλαγή ρόλου', adminBadge: 'ADMIN',
    toastSaved: 'Αποθηκεύτηκε', toastDeleted: 'Διαγράφηκε', toastRoleChanged: 'Ο ρόλος άλλαξε', toastWelcome: 'Καλώς ήρθατε', toastNoMail: 'Δεν υπάρχει email.',
    refresh: 'Ανανέωση',
    settingsDesign: 'Σχέδιο', settingsLang: 'Γλώσσα', settingsPass: 'Αλλαγή PIN',
    coverageLegend: 'Προσωπικό:',
    tabSchedule: 'Πρόγραμμα', tabGraph: 'Γράφημα', staffCount: 'Προσωπικό',
    pinError: 'Το PIN πρέπει να έχει 6 ψηφία.',
    selectMonth: 'Επιλογή Μήνα',
    monthDone: 'Όλα ολοκληρώθηκαν!',
    monthOver: 'Χρειάζεσαι διάλειμμα!',
    confirmDeleteEmployee: 'Διαγραφή υπαλλήλου;',
    confirmDeleteEmployeeText: 'Θέλετε πραγματικά να διαγράψετε αυτόν τον υπάλληλο; Αυτή η ενέργεια δεν μπορεί να αναιρεθεί.',
    employeeDeleted: 'Ο υπάλληλος διαγράφηκε.',
    cannotDeleteSelf: 'Δεν μπορείτε να διαγράψετε τον εαυτό σας.',
    deactivateEmployee: 'Απενεργοποίηση υπαλλήλου',
    reactivateEmployee: 'Επανενεργοποίηση',
    deactivatedBadge: 'ΑΝΕΝΕΡΓΟΣ',
    deactivatedFrom: 'Απενεργοποιήθηκε από',
    deactivatedSince: 'Ανενεργός από',
    deactivateFromDate: 'Από ποια ημερομηνία να απενεργοποιηθεί ο υπάλληλος;',
    deactivateWarning: 'Από αυτή την ημερομηνία, ο υπάλληλος δεν μπορεί να συνδεθεί και δεν εμφανίζεται στην επισκόπηση.',
    employeeDeactivated: 'Υπάλληλος απενεργοποιήθηκε από',
    employeeReactivated: 'Υπάλληλος επανενεργοποιήθηκε.',
    deactivatedEmployees: 'Ανενεργοί υπάλληλοι',
    accountDisabled: 'Αυτός ο λογαριασμός έχει απενεργοποιηθεί. Επικοινωνήστε με έναν διαχειριστή.',
    confirmDeactivate: 'Επιβεβαίωση απενεργοποίησης',
    signatureEmployee: 'Υπάλληλος',
    signatureEmployer: 'Εργοδότης',
    // Shift Scheduling
    scheduleTab: 'Πρόγραμμα Βάρδιων',
    myShiftsTab: 'Οι Βάρδιες μου',
    addShift: 'Προσθήκη Βάρδιας',
    editShift: 'Επεξεργασία Βάρδιας',
    deleteShift: 'Διαγραφή Βάρδιας',
    shiftType: 'Τύπος Βάρδιας',
    shiftNotes: 'Σημειώσεις',
    selectEmployee: 'Επιλογή Υπαλλήλου',
    shiftConflict: 'Σύγκρουση: Ο υπάλληλος έχει ήδη βάρδια αυτήν την ώρα',
    noShiftsScheduled: 'Δεν υπάρχουν βάρδιες',
    upcomingShifts: 'Επερχόμενες Βάρδιες',
    today: 'Σήμερα',
    dayOff: 'Ρεπό',
    week: 'Εβδομάδα',
    previousWeek: 'Προηγούμενη',
    nextWeek: 'Επόμενη',
    monday: 'Δευτέρα',
    tuesday: 'Τρίτη',
    wednesday: 'Τετάρτη',
    thursday: 'Πέμπτη',
    friday: 'Παρασκευή',
    saturday: 'Σάββατο',
    sunday: 'Κυριακή',
    lastLogin: 'Τελευταία Σύνδεση',
    lastTimeEntry: 'Τελευταία Καταχώρηση',
    never: 'Ποτέ',
    addEntry: 'Προσθήκη Εγγραφής',
    breakLabel: 'Διάλειμμα (Λεπτά)',
    breakStartLabel: 'Έναρξη Διαλείμματος',
    breakEndLabel: 'Λήξη Διαλείμματος',
    breakWarning6h: 'Προσοχή: Πάνω από 6 ώρες εργασίας απαιτούνται 30 λεπτά διάλειμμα.',
    breakWarning9h: 'Προσοχή: Πάνω από 9 ώρες εργασίας απαιτούνται 45 λεπτά διάλειμμα.',
    negativeDuration: 'Σφάλμα: Η διάρκεια εργασίας είναι αρνητική.',
    addBreak: 'Προσθήκη Διαλείμματος',
    breaks: 'Διαλείμματα',
    futureWorkError: 'Η εργασία δεν μπορεί να είναι στο μέλλον.',
  },
  ar: {
    loginTitle: 'تسجيل الدخول', nameLabel: 'الاسم', firstNameLabel: 'الاسم الأول', lastNameLabel: 'اسم العائلة', passwordLabel: 'رمز PIN (6 أرقام)', emailLabel: 'البريد الإلكتروني', loginButton: 'دخول', registerButton: 'تسجيل', toggleRegister: 'تسجيل جديد', toggleLogin: 'دخول', employeeDashboard: 'لوحة التحكم', adminConsole: 'الادارة', loggedInAs: 'مستخدم', monthlyStatus: 'الحالة', limitReached: 'تنبيه!', limitExceeded: 'تجاوز!', allGood: 'جيد', recordTime: 'تسجيل', dateLabel: 'التاريخ', startLabel: 'البدء', endLabel: 'الانتهاء', saveButton: 'حفظ', history: 'السجل', employeeOverview: 'الموظفين', entriesAll: 'الكل', print: 'طباعة', summary: 'الملخص', total: 'الإجمالي', printTitle: 'التقرير', stand: 'تاريخ', employee: 'الموظف', time: 'الوقت', duration: 'المدة', newPasswordTitle: 'رمز PIN جديد', newPasswordPlaceholder: '123456', cancel: 'إلغاء', editEntry: 'تعديل', confirmDelete: 'حذف؟', passChanged: 'تم تغيير PIN!', saveError: 'خطأ.', fillData: 'بيانات ناقصة.', userNotFound: 'غير موجود.', wrongPass: 'PIN خطأ.', nameTaken: 'مستخدم.', regError: 'خطأ.', printError: 'خطأ.', max: 'حد', current: 'حاليا', delete: 'حذف', edit: 'تعديل', logout: 'خروج', sendReport: 'إرسال', reportSubject: 'تقرير', reportBody: 'مرحبا', reportBody2: 'مرفق التقرير.', close: 'إغلاق', printReport: 'طباعة',
    coverageTitle: 'نظرة عامة', coverageDate: 'التاريخ', coverageGap: 'فجوة!', coverageStaff: 'موظف',
    viewDay: 'يوم', viewWeek: 'أسبوع', viewMonth: 'شهر', viewAll: 'الكل',
    mo: 'ن', tu: 'ث', we: 'ع', th: 'خ', fr: 'ج', sa: 'س', su: 'ح',
    overlapError: 'خطأ: الوقت يتداخل مع إدخال موجود!',
    makeAdmin: 'تغيير الدور', adminBadge: 'مسؤول',
    toastSaved: 'تم الحفظ', toastDeleted: 'تم الحذف', toastRoleChanged: 'تم تغيير الدور', toastWelcome: 'أهلا بك', toastNoMail: 'لا يوجد بريد إلكتروني.',
    refresh: 'تحديث',
    settingsDesign: 'التصميم', settingsLang: 'اللغة', settingsPass: 'تغيير كلمة المرور',
    coverageLegend: 'الموظفين:',
    tabSchedule: 'الجدول', tabGraph: 'الرسم البياني', staffCount: 'عدد الموظفين',
    pinError: 'يجب أن يتكون PIN من 6 أرقام.',
    selectMonth: 'اختر الشهر',
    monthDone: 'كل شيء تم!',
    monthOver: 'أنت بحاجة إلى استراحة!',
    confirmDeleteEmployee: 'حذف الموظف؟',
    confirmDeleteEmployeeText: 'هل أنت متأكد أنك تريد حذف هذا الموظف؟ لا يمكن التراجع عن هذا الإجراء.',
    employeeDeleted: 'تم حذف الموظف.',
    cannotDeleteSelf: 'لا يمكنك حذف نفسك.',
    deactivateEmployee: 'تعطيل الموظف',
    reactivateEmployee: 'إعادة التفعيل',
    deactivatedBadge: 'غير نشط',
    deactivatedFrom: 'معطل من',
    deactivatedSince: 'غير نشط منذ',
    deactivateFromDate: 'من أي تاريخ يجب تعطيل الموظف؟',
    deactivateWarning: 'من هذا التاريخ، لن يتمكن الموظف من تسجيل الدخول ولن يظهر في صفحة التغطية.',
    employeeDeactivated: 'تم تعطيل الموظف من',
    employeeReactivated: 'تم إعادة تفعيل الموظف.',
    deactivatedEmployees: 'الموظفون غير النشطون',
    accountDisabled: 'تم تعطيل هذا الحساب. يرجى التواصل مع المسؤول.',
    confirmDeactivate: 'تأكيد التعطيل',
    signatureEmployee: 'الموظف',
    signatureEmployer: 'صاحب العمل',
    // Shift Scheduling
    scheduleTab: 'جدول المناوبات',
    myShiftsTab: 'مناوباتي',
    addShift: 'إضافة مناوبة',
    editShift: 'تعديل مناوبة',
    deleteShift: 'حذف مناوبة',
    shiftType: 'نوع المناوبة',
    shiftNotes: 'ملاحظات',
    selectEmployee: 'اختر موظف',
    shiftConflict: 'تعارض: الموظف لديه مناوبة بالفعل في هذا الوقت',
    noShiftsScheduled: 'لا توجد مناوبات مجدولة',
    upcomingShifts: 'المناوبات القادمة',
    today: 'اليوم',
    dayOff: 'يوم عطلة',
    week: 'أسبوع',
    previousWeek: 'الأسبوع السابق',
    nextWeek: 'الأسبوع التالي',
    monday: 'الاثنين',
    tuesday: 'الثلاثاء',
    wednesday: 'الأربعاء',
    thursday: 'الخميس',
    friday: 'الجمعة',
    saturday: 'السبت',
    sunday: 'الأحد',
    lastLogin: 'آخر تسجيل دخول',
    lastTimeEntry: 'آخر إدخال',
    never: 'أبدًا',
    addEntry: 'إضافة إدخال',
    breakLabel: 'استراحة (دقيقة)',
    breakStartLabel: 'بداية الاستراحة',
    breakEndLabel: 'نهاية الاستراحة',
    breakWarning6h: 'تنبيه: العمل أكثر من 6 ساعات يتطلب استراحة 30 دقيقة.',
    breakWarning9h: 'تنبيه: العمل أكثر من 9 ساعات يتطلب استراحة 45 دقيقة.',
    negativeDuration: 'خطأ: مدة العمل غير صحيحة.',
    addBreak: 'إضافة استراحة',
    breaks: 'استراحات',
    futureWorkError: 'لا يمكن تسجيل العمل في المستقبل.',
  }
};

// --- HELPERS ---

// Returns true if the employee is deactivated on the given date (defaults to today)
const isEmployeeDisabled = (emp, dateStr = null) => {
  if (!emp || !emp.disabledFrom) return false;
  const checkDate = dateStr || new Date().toISOString().split('T')[0];
  return checkDate >= emp.disabledFrom;
};

const formatHours = (num) => `${Number(num || 0).toFixed(2)}\u00A0h`;

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const timeToDecimal = (timeStr) => {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h + m / 60;
};

const sortEntries = (entries) => {
  return [...entries].sort((a, b) => {
    if (a.date > b.date) return -1;
    if (a.date < b.date) return 1;
    return a.startTime > b.startTime ? -1 : 1;
  });
};

const filterEntries = (entries, filterMode, month) => {
  let filtered = entries;
  const now = new Date();

  if (filterMode === 'month') {
    const currentMonthKey = month || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    filtered = entries.filter(e => e.monthKey === currentMonthKey);
  } else if (filterMode === 'week') {
    const day = now.getDay() || 7;
    const monday = new Date(now);
    monday.setHours(0, 0, 0, 0);
    monday.setDate(now.getDate() - day + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    filtered = entries.filter(e => {
      const d = new Date(e.date);
      d.setHours(12, 0, 0, 0);
      return d >= monday && d <= sunday;
    });
  }

  return sortEntries(filtered);
};

const getWeekDays = (baseDate) => {
  const d = new Date(baseDate);
  const day = d.getDay();
  const diff = d.getDate() - (day === 0 ? 6 : day - 1);
  const monday = new Date(d);
  monday.setDate(diff);

  const week = [];
  for (let i = 0; i < 7; i++) {
    const temp = new Date(monday);
    temp.setDate(monday.getDate() + i);
    week.push(temp.toISOString().split('T')[0]);
  }
  return week;
};

const getMonthDays = (baseDate) => {
  const d = new Date(baseDate);
  const year = d.getFullYear();
  const month = d.getMonth();
  const days = [];
  const date = new Date(year, month, 1);
  while (date.getMonth() === month) {
    days.push(date.toISOString().split('T')[0]);
    date.setDate(date.getDate() + 1);
  }
  return days;
};

const getDayName = (dateStr, t) => {
  const d = new Date(dateStr);
  const day = d.getDay();
  const days = ['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'];
  return t(days[day]);
};



const getEffectiveMaxHours = (employee, monthKey) => {
  if (!employee) return 0;
  let effective = employee.maxHours || 30; // Fallback
  // If employee has a targetHoursHistory array, find the latest applied
  if (employee.targetHoursHistory && employee.targetHoursHistory.length > 0) {
    // History should be sorted by monthKey descending
    const sortedHistory = [...employee.targetHoursHistory].sort((a, b) => b.monthKey.localeCompare(a.monthKey));
    // Find the first rule that is <= the current monthKey
    const applicableRule = sortedHistory.find(h => h.monthKey <= monthKey);
    if (applicableRule) {
      effective = applicableRule.hours;
    }
  }
  return Number(effective);
};

const calculateMonthlyBalances = (userId, entries, maxHoursOrEmployee, targetMonth, monthlyClosings = [], employmentType = 'FULL_TIME') => {
  const isEmployeeObj = typeof maxHoursOrEmployee === 'object' && maxHoursOrEmployee !== null;
  const employee = isEmployeeObj ? maxHoursOrEmployee : null;
  const fallbackMaxHours = isEmployeeObj ? (employee.maxHours || 30) : (maxHoursOrEmployee || 30);
  const userEntries = entries.filter(e => e.userId === userId);
  // Create a map for quick lookup of paid hours
  const closingsMap = {};
  monthlyClosings.forEach(c => {
    if (c.userId === userId) {
      closingsMap[c.monthKey] = c.paidHours;
    }
  });

  if (userEntries.length === 0 && monthlyClosings.length === 0) {
    return {};
  }

  // Ensure every entry has a monthKey
  // Ensure every entry has a monthKey
  // Helper for robust key derivation from date string
  const getMoonKeyFromDate = (dateStr) => {
    if (!dateStr) return null;

    // YYYY-MM-DD or YYYY-M-D (flexible ISO)
    const isoMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      return `${isoMatch[1]}-${isoMatch[2].padStart(2, '0')}`;
    }

    // DD.MM.YYYY or D.M.YYYY (German)
    const deMatch = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    if (deMatch) {
      const year = deMatch[3];
      const month = deMatch[2].padStart(2, '0');
      return `${year}-${month}`;
    }

    // Fallback: Check if it's already YYYY-MM or close
    if (dateStr.match(/^\d{4}-\d{2}$/)) return dateStr;

    // Last resort: standard Date object parsing (sometimes risky with TZs but good for weird formats)
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return d.toISOString().substring(0, 7);
    }

    return null;
  };

  // Debug helper to spy on what keys we are generating
  const generatedKeysDebug = new Set();

  const processedEntries = userEntries.map(e => {
    const derived = getMoonKeyFromDate(e.date);
    // Strict priority: Derived (Standardized) > Existing (if strictly valid)
    const existing = (e.monthKey && e.monthKey.match(/^\d{4}-\d{2}$/)) ? e.monthKey : null;
    const finalKey = derived || existing;

    if (userId === 'debug_user_id') generatedKeysDebug.add(finalKey); // Concept only

    return { ...e, monthKey: finalKey };
  }).filter(e => e.monthKey);

  const userMonths = [...new Set(processedEntries.map(e => e.monthKey))];
  const closingMonths = monthlyClosings.filter(c => c.userId === userId).map(c => c.monthKey);

  // FIX: Always include targetMonth in the range consideration
  const distinctMonths = new Set([...userMonths, ...closingMonths]);
  if (targetMonth) distinctMonths.add(targetMonth);

  const allMonths = [...distinctMonths].sort();

  if (allMonths.length === 0) {
    // Should be covered by early return above, but just in case
    return {};
  }

  const firstMonthStr = allMonths[0]; // First chronological month
  // We don't need currentMonthStr logic for limiting the loop anymore if we just want to cover the data + target
  // But we want to ensure we reach at least targetMonth OR currentMonth

  // Actually, 'allMonths' now helps define the start.
  // The End should be targetMonth (to show that specific month) 
  // OR the last month with data? 
  // The original loop went up to 'finalMonthStr' (target || current).
  // Let's stick to that but ensure start is correct.

  const finalMonthStr = targetMonth || new Date().toISOString().substring(0, 7);

  // Ensure we don't stop BEFORE targetMonth if data goes further? 
  // No, we want to calculate balances UP TO the view month usually. 
  // But if we have future entries, they affect the "Konto"? 
  // For 'getEmployeeStats', we want the balance AT targetMonth.
  // So traversing up to max(lastDataMonth, targetMonth) is safer?
  // Let's stick to the original logic: iterate from FirstDataMonth up to TargetMonth (or Current).
  // If targetMonth < FirstDataMonth, our loop condition d <= last handles it (start > end -> loop doesn't run)
  // BUT we just added targetMonth to allMonths! So firstMonthStr <= targetMonth is guaranteed.

  // Robust Month Generation (Integer based to avoid Date/TZ issues)
  const [startYear, startMonth] = firstMonthStr.split('-').map(Number);
  const [endYear, endMonth] = finalMonthStr.split('-').map(Number);

  let tempYear = startYear;
  let tempMonth = startMonth;

  const months = [];

  // Calculate total months to iterate to avoid infinite loops
  const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
  const loopLimit = Math.max(0, Math.min(totalMonths, 1000)); // Safety cap

  for (let i = 0; i < loopLimit; i++) {
    months.push(`${tempYear}-${String(tempMonth).padStart(2, '0')}`);
    tempMonth++;
    if (tempMonth > 12) {
      tempMonth = 1;
      tempYear++;
    }
  }

  const balances = {};
  let carryOver = 0;

  months.forEach(month => {
    const monthEntries = processedEntries.filter(e => e.monthKey === month);
    const worked = monthEntries.reduce((sum, e) => {
      const duration = Number(e.duration) || 0;
      // Absence Logic:
      // If SICK or VACATION:
      // - Full Time: Counts as work (prevent minus hours)
      // - Mini/Midi: Ignored (does not count towards overtime/balance)
      if (e.type === 'SICK' || e.type === 'VACATION') {
        // Previously filtered by employmentType, now allow ALL
        // if (employmentType === 'MINI_JOB' || employmentType === 'MIDI_JOB') { return sum; }
      }
      return sum + duration;
    }, 0);
    const totalAvailable = worked + carryOver; // Restored missing variable

    // Logic Update (User Request): 
    // "Es soll nur Stunden die zu viel gearbeitet wurden auf den nächsten Monat übertragen."
    // 1. Manual Settlement: User defines exactly how many hours are "spent/paid" (paidOrDue).
    //    Balance = (Check-in + Worked) - Paid
    // 2. Auto-Calculation: Only track OVERTIME (Plus hours). Do not track Undertime.
    //    Balance = Check-in + Math.max(0, Worked - MaxHours)

    const hasPaidEntry = closingsMap[month] !== undefined;

    let newBalance;
    let paidOrDue; // Fix: Declaration missing in previous edit
    if (hasPaidEntry) {
      // Manual Settlement: Subtract the explicit paid amount from total available
      // This allows paying out overtime or resetting balances
      const paid = closingsMap[month];
      newBalance = (carryOver + worked) - paid;
      paidOrDue = paid; // for display
    } else {
      // Auto Calculation Logic
      const currentMaxHours = employee ? getEffectiveMaxHours(employee, month) : fallbackMaxHours;
      const surplus = worked - currentMaxHours;

      if (employmentType === 'MINI_JOB' || employmentType === 'MIDI_JOB') {
        // Option B: Mini/Midi Job (User Request: "Nur Überstunden zählen, kein Minus")
        // 1. Monthly contribution cannot be negative (max(0, surplus)).
        const monthlyContribution = Math.max(0, surplus);
        // 2. Add to existing carryOver.
        newBalance = carryOver + monthlyContribution;
        // 3. Safety: Ensure balance never goes negative (clamped to 0)
        newBalance = Math.max(0, newBalance);
      } else {
        // Option A: Full Time / Festangestellt (Standard Gleitzeit)
        // User Request: "Wer weniger arbeitet, macht Minusstunden."
        // New Balance = Old Balance + Surplus (which can be negative)
        newBalance = carryOver + surplus;
      }

      paidOrDue = currentMaxHours; // purely for display/concept
    }

    /* 
       Note: "paidOrDue" in the 'balances' object is mainly used visually to see what was subtracted.
       In Auto-mode, effectively we subtracted (Worked - Surplus).
    */

    balances[month] = {
      worked,
      carryOverBefore: carryOver, // For display if needed
      carryOver: carryOver, // RESTORED for UI compatibility (incoming balance)
      totalAvailable,
      total: totalAvailable, // RESTORED for UI compatibility
      paidOrDue,
      hasPaidEntry,
      balance: newBalance // This becomes the carryOver for the next month
    };
    carryOver = newBalance;
  });

  return balances;
};



// --- HELPER ---
const formatTimestamp = (timestamp, lang = 'de') => {
  if (!timestamp || !timestamp.seconds) {
    return translations[lang]?.['never'] || 'Nie';
  }
  const date = new Date(timestamp.seconds * 1000);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'gerade eben';
  if (diffMins < 60) return `vor ${diffMins} Min`;
  if (diffHours < 24) return `vor ${diffHours} Std`;
  if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;

  return date.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// --- TOAST COMPONENT ---
const Toast = ({ notification, onClose }) => {
  if (!notification) return null;

  const bgColors = {
    success: 'bg-green-600',
    error: 'bg-red-600',
    info: 'bg-blue-600'
  };

  const icons = {
    success: <CheckCircle className="w-5 h-5 text-white mr-2" />,
    error: <AlertTriangle className="w-5 h-5 text-white mr-2" />,
    info: <Info className="w-5 h-5 text-white mr-2" />
  };

  return (
    <div className={`fixed top-5 left-1/2 transform -translate-x-1/2 z-[9999] flex items-center ${bgColors[notification.type]} text-white px-6 py-3 rounded-full shadow-xl animate-in slide-in-from-top-5 fade-in duration-300`}>
      {icons[notification.type]}
      <span className="font-medium">{notification.message}</span>
      <span className="font-medium">{notification.message}</span>
      <Button variant="ghost" size="iconSm" onClick={onClose} className="ml-4 text-white/80 hover:text-white hover:bg-white/10"><X className="w-4 h-4" /></Button>
    </div>
  );
};

const SplashScreen = ({ logoUrl }) => (
  <div className="fixed inset-0 z-[10000] flex flex-col items-center justify-center bg-white dark:bg-black animate-out fade-out duration-1000 fill-mode-forwards">
    <div className="flex flex-col items-center">
      <img src={logoUrl} alt="Logo" className="w-32 h-32 sm:w-48 sm:h-48 object-contain mb-8 animate-fade-pulse" />
      <div className="h-2 w-48 bg-gray-100 dark:bg-zinc-900 rounded-full overflow-hidden">
        <div className="h-full bg-accent-mint animate-progress-bar"></div>
      </div>
    </div>
  </div>
);

// --- STABILE SUB-KOMPONENTEN ---
const Footer = ({ onOpenImpressum, onOpenDatenschutz }) => (
  <footer className="mt-auto py-6 text-center text-xs text-gray-400 dark:text-slate-500 border-t border-gray-100 dark:border-slate-800 print:hidden">
    <p>
      Designed and Published by{' '}
      <a
        href="https://lm-cloud.eu"
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-blue-500 transition-colors font-medium"
      >
        LM-Cloud.eu
      </a>
      <span className="mx-2">|</span>
      <Button variant="link" onClick={onOpenImpressum} className="hover:text-blue-500 transition-colors font-medium focus:outline-none text-xs text-gray-400 dark:text-slate-500">Impressum</Button>
      <span className="mx-1">·</span>
      <Button variant="link" onClick={onOpenDatenschutz} className="hover:text-blue-500 transition-colors font-medium focus:outline-none text-xs text-gray-400 dark:text-slate-500">Datenschutz</Button>
    </p>
  </footer>
);

const MainWrapper = ({ children, isRTL, onOpenImpressum, onOpenDatenschutz }) => (
  <div
    className="min-h-screen bg-slate-50 dark:bg-black font-sans transition-colors duration-300 flex flex-col"
    dir={isRTL ? 'rtl' : 'ltr'}
    style={{
      paddingTop: 'env(safe-area-inset-top)',
      paddingBottom: 'env(safe-area-inset-bottom)',
      paddingLeft: 'env(safe-area-inset-left)',
      paddingRight: 'env(safe-area-inset-right)'
    }}
  >
    <div className="flex-grow w-full">
      {children}
    </div>
    <Footer onOpenImpressum={onOpenImpressum} onOpenDatenschutz={onOpenDatenschutz} />
  </div>
);

const LanguageSwitcher = ({ lang, setLang }) => (
  <div className="flex items-center space-x-1 bg-gray-100 dark:bg-zinc-900 rounded-lg p-1">
    {['de', 'el', 'ar'].map(l => (
      <Button
        key={l}
        onClick={() => setLang(l)}
        variant="ghost"
        size="sm"
        className={`px-2 py-1 text-xs rounded uppercase transition-colors h-auto ${lang === l ? 'bg-white dark:bg-zinc-800 shadow text-accent-mint dark:text-accent-mint font-bold' : 'text-gray-500 dark:text-gray-400'}`}
      >
        {l}
      </Button>
    ))}
  </div>
);


const DarkModeToggle = ({ darkMode, toggleDarkMode }) => (
  <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="p-2 rounded-full bg-gray-100 dark:bg-zinc-900 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-zinc-800 transition-colors">
    {darkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
  </Button>
);

const ProgressBar = ({ current, max }) => {
  const pct = Math.min(100, (current / (max || 1)) * 100);
  let color = pct >= 100 ? "bg-red-500" : pct > 80 ? "bg-yellow-500" : "bg-green-500";
  if (pct > 100 && pct < 105) color = "bg-purple-500"; // Special color for slight over achievement
  return (
    <div className="w-full bg-gray-200 dark:bg-zinc-900 rounded-full h-4 mt-2 overflow-hidden print:hidden">
      <div className={`${color} h-4 transition-all duration-500`} style={{ width: `${pct}%` }}></div>
    </div>
  );
};

const FilterToggle = ({ filterMode, setFilterMode, t }) => (
  <div className="flex bg-gray-100 dark:bg-zinc-900 rounded-lg p-1 print:hidden">
    <Button variant="ghost" size="sm" onClick={() => setFilterMode('week')} className={`px-3 py-1.5 transition-all ${filterMode === 'week' ? 'bg-white dark:bg-zinc-800 shadow text-accent-mint dark:text-accent-mint' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>{t('viewWeek')}</Button>
    <Button variant="ghost" size="sm" onClick={() => setFilterMode('month')} className={`px-3 py-1.5 transition-all ${filterMode === 'month' ? 'bg-white dark:bg-zinc-800 shadow text-accent-mint dark:text-accent-mint' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>{t('viewMonth')}</Button>
    <Button variant="ghost" size="sm" onClick={() => setFilterMode('all')} className={`px-3 py-1.5 transition-all ${filterMode === 'all' ? 'bg-white dark:bg-zinc-800 shadow text-accent-mint dark:text-accent-mint' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>{t('viewAll')}</Button>
  </div>
);


const CoverageView = ({ entries, employees, t, isRTL, externalMonth, onMonthChange, lang }) => {
  const [selectedDate, setSelectedDate] = useState(externalMonth ? `${externalMonth}-01` : new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('day');


  // Sync with external month change
  useEffect(() => {
    if (externalMonth) {
      const currentSelectedMonth = selectedDate.substring(0, 7);
      if (currentSelectedMonth !== externalMonth) {
        // Set to first day of the new external month
        setSelectedDate(`${externalMonth}-01`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMonth]);

  // Notify parent when month changes internally
  useEffect(() => {
    if (onMonthChange) {
      const currentSelectedMonth = selectedDate.substring(0, 7);
      if (currentSelectedMonth !== externalMonth) {
        onMonthChange(currentSelectedMonth);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Range: 09:00 to 21:30
  // eslint-disable-next-line no-unused-vars
  const hours = Array.from({ length: 13 }, (_, i) => i + 9);
  const hoursWithHalf = [];
  for (let h = 9; h <= 21.5; h += 0.5) hoursWithHalf.push(h);

  const getEntriesForDay = (empId, dateStr) => {
    return entries.filter(e => e.userId === empId && e.date === dateStr);
  };

  const isWorkingSlot = (empId, slot, dateStr) => {
    const empEntries = getEntriesForDay(empId, dateStr);
    return empEntries.some(e => {
      const start = timeToDecimal(e.startTime);
      const end = timeToDecimal(e.endTime);
      return start <= slot && end > slot;
    });
  };

  const isBreakSlot = (empId, slot, dateStr) => {
    const empEntries = getEntriesForDay(empId, dateStr);
    return empEntries.some(e => {
      if (!e.breakStartTime || !e.breakEndTime) return false;
      const breakStart = timeToDecimal(e.breakStartTime);
      const breakEnd = timeToDecimal(e.breakEndTime);
      // Check if slot is strictly within the break time (exclusive of end to avoid double painting if needed, but inclusive is usually safer for visibility)
      // Using >= and < for half-hour slots to capture the specific block
      return slot >= breakStart && slot < breakEnd;
    });
  };

  let columns = [];
  let renderCell = null;
  let tableHeaders = [];

  if (viewMode === 'day') {
    columns = hoursWithHalf;
    const formatHours = (hours) => {
      if (typeof hours !== 'number') return '0:00';
      const h = Math.floor(hours);
      const m = Math.round((hours - h) * 60);
      return `${h}:${m < 10 ? '0' : ''}${m}`;
    };

    // Fix: Populate tableHeaders for day view
    tableHeaders = columns.map(h => formatHours(h));

    // eslint-disable-next-line no-unused-vars
    const formatTimestamp = (timestamp, lang = 'de') => {
      if (!timestamp || !timestamp.seconds) return t('never');
      const date = new Date(timestamp.seconds * 1000);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'gerade eben';
      if (diffMins < 60) return `vor ${diffMins} Min`;
      if (diffHours < 24) return `vor ${diffHours} Std`;
      if (diffDays < 7) return `vor ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`;

      return date.toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' });
    };
    renderCell = (empId, colValue) => {
      const active = isWorkingSlot(empId, colValue, selectedDate);
      const isBreak = isBreakSlot(empId, colValue, selectedDate);

      let bgClass = 'bg-gray-50 dark:bg-zinc-900/30';
      if (isBreak) {
        bgClass = 'bg-cyan-500/80 dark:bg-accent-lilac/80'; // Lilac for break
      } else if (active) {
        bgClass = 'bg-blue-500 dark:bg-accent-mint opacity-90'; // Mint for work
      }

      return <div className={`h-full w-full ${bgClass}`}></div>;
    };
  } else if (viewMode === 'week') {
    columns = getWeekDays(selectedDate);
    tableHeaders = columns.map(date => {
      const d = new Date(date);
      return `${getDayName(date, t)} ${String(d.getDate()).padStart(2, '0')}`;
    });
    renderCell = (empId, dateStr) => {
      const dayEntries = getEntriesForDay(empId, dateStr);
      if (dayEntries.length === 0) return null;
      return (
        <div className="text-[10px] leading-tight bg-blue-100 dark:bg-accent-mint/20 text-blue-800 dark:text-accent-mint p-1 rounded text-center truncate">
          {dayEntries.map((e, i) => <div key={i}>{e.startTime}-{e.endTime}</div>)}
        </div>
      );
    };
  } else if (viewMode === 'month') {
    columns = getMonthDays(selectedDate);
    tableHeaders = columns.map(date => String(new Date(date).getDate()).padStart(2, '0'));
    renderCell = (empId, dateStr) => {
      const dayEntries = getEntriesForDay(empId, dateStr);
      if (dayEntries.length === 0) return null;
      const total = dayEntries.reduce((sum, e) => sum + (Number(e.duration) || 0), 0);
      return (
        <div className={`text-[9px] font-medium text-center ${total >= 8 ? 'text-green-600 dark:text-accent-mint' : 'text-blue-600 dark:text-accent-lilac'}`}>
          {formatHours(total)}
        </div>
      );
    };
  }

  // Calculate coverage for heatmap row (only for day view or specific needs)
  const coverageCounts = columns.map(col => {
    if (viewMode === 'day') {
      // Count employees working in this slot (excluding disabled on selectedDate)
      const count = employees.filter(e => e.role !== 'admin' && e.role !== 'display' && !isEmployeeDisabled(e, selectedDate) && isWorkingSlot(e.id, col, selectedDate)).length;
      return { col, count };
    } else {
      // For week/month, just count active employees for that day (excluding disabled on that date)
      const count = employees.filter(e => e.role !== 'admin' && e.role !== 'display' && !isEmployeeDisabled(e, col) && getEntriesForDay(e.id, col).length > 0).length;
      return { col, count };
    }
  });

  const getStatusColor = (count) => {
    if (count === 0) return 'bg-red-50 text-red-300 dark:bg-red-900/20 dark:text-red-500/50';
    if (count === 1) return 'bg-yellow-50 text-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400';
    return 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400';
  };

  // eslint-disable-next-line no-unused-vars
  const getDotColor = (count) => {
    if (count === 0) return 'bg-red-500 border-red-600';
    if (count === 1) return 'bg-yellow-400 border-yellow-500';
    if (count === 2) return 'bg-green-500 border-green-600';
    return 'bg-purple-600 border-purple-700';
  };

  // --- SVG GRAPH CALCULATION ---
  // eslint-disable-next-line no-unused-vars
  const maxCount = Math.max(...coverageCounts.map(c => c.count), 4);
  // eslint-disable-next-line no-unused-vars
  const pointDistance = 100 / (coverageCounts.length - 1);

  // eslint-disable-next-line no-unused-vars
  const points = coverageCounts.map((c, i) => {
    const max = Math.max(...coverageCounts.map(x => x.count), 4); // Min height for scale
    const y = 100 - (c.count / max) * 100;
    return `${i * pointDistance},${y}`;
  }).join(' ');

  // eslint-disable-next-line no-unused-vars
  const getGraphBarHeight = (count) => {
    const max = Math.max(...coverageCounts.map(c => c.count), 4);
    return `${(count / max) * 100}%`;
  };

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-slate-200 dark:border-zinc-800 p-4 sm:p-6 mb-8 overflow-hidden">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
          <Activity className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'} text-accent-mint`} />
          {t('coverageTitle')}
          <span className="ml-3 text-sm font-normal text-gray-500 dark:text-slate-400 hidden sm:inline-block">
            {new Date(selectedDate).toLocaleDateString(lang === 'de' ? 'de-DE' : 'en-US', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
          </span>
        </h3>

        <div className="flex flex-wrap items-center gap-1 sm:gap-2 w-full sm:w-auto justify-between sm:justify-end">


          <div className="flex bg-gray-100 dark:bg-zinc-800/50 rounded-lg p-1">
            <Button variant="ghost" size="sm" onClick={() => setViewMode('day')} className={`px-3 py-1.5 transition-all ${viewMode === 'day' ? 'bg-white dark:bg-zinc-700 shadow-sm text-accent-mint dark:text-accent-mint' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>{t('viewDay')}</Button>
            <Button variant="ghost" size="sm" onClick={() => setViewMode('week')} className={`px-3 py-1.5 transition-all ${viewMode === 'week' ? 'bg-white dark:bg-zinc-700 shadow-sm text-accent-mint dark:text-accent-mint' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>{t('viewWeek')}</Button>
            <Button variant="ghost" size="sm" onClick={() => setViewMode('month')} className={`px-3 py-1.5 transition-all ${viewMode === 'month' ? 'bg-white dark:bg-zinc-700 shadow-sm text-accent-mint dark:text-accent-mint' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'}`}>{t('viewMonth')}</Button>
          </div>

          <div className="flex items-center bg-white dark:bg-slate-700/50 rounded-lg border border-gray-200 dark:border-slate-600 p-0.5">
            <Button
              variant="ghost"
              size="iconSm"
              onClick={() => {
                const date = new Date(selectedDate);
                if (viewMode === 'day') date.setDate(date.getDate() - 1);
                else if (viewMode === 'week') date.setDate(date.getDate() - 7);
                else if (viewMode === 'month') date.setMonth(date.getMonth() - 1);
                setSelectedDate(date.toISOString().split('T')[0]);
              }}
              className="text-gray-500 dark:text-slate-300"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center px-1 sm:px-2 border-l border-r border-gray-100 dark:border-slate-600 mx-0.5">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="p-0.5 sm:p-1 border-none bg-transparent text-xs sm:text-sm text-gray-800 dark:text-white outline-none focus:ring-0 w-[100px] sm:w-[110px] text-center"
              />
            </div>
            <Button
              variant="ghost"
              size="iconSm"
              onClick={() => {
                const date = new Date(selectedDate);
                if (viewMode === 'day') date.setDate(date.getDate() + 1);
                else if (viewMode === 'week') date.setDate(date.getDate() + 7);
                else if (viewMode === 'month') date.setMonth(date.getMonth() + 1);
                setSelectedDate(date.toISOString().split('T')[0]);
              }}
              className="text-gray-500 dark:text-slate-300"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="overflow-x-auto overflow-y-hidden min-h-[200px] pb-4">

        {/* === TABLE VIEW === */}
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr>
              <th className="text-left p-3 dark:text-slate-200 sticky left-0 bg-white dark:bg-zinc-900 z-20 w-32 sm:w-40 border-b border-gray-100 dark:border-zinc-800 border-r dark:border-r-zinc-800 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.05)] text-xs font-semibold uppercase tracking-wider">{t('employee')}</th>
              {tableHeaders.map((h, i) => {
                const isTuesday = viewMode !== 'day' && columns[i] && new Date(columns[i]).getDay() === 2;
                return (
                  <th key={i} className={`p-2 text-center text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium border-b dark:border-b-zinc-800 min-w-[32px] ${(viewMode === 'week' && (h.includes(t('sa')) || h.includes(t('su')))) ? 'bg-gray-50/50 dark:bg-zinc-800/50' : ''} ${isTuesday ? 'bg-gray-50 dark:bg-zinc-800/30' : ''}`}>
                    {h}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {/* Heatmap Row (Moved to Top) */}
            <tr className="font-bold border-b border-gray-100 dark:border-slate-700/50">
              <td className="p-2 text-xs dark:text-slate-300 sticky left-0 bg-white dark:bg-zinc-900 z-20 border-r border-gray-100 dark:border-zinc-800 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.05)]">MA (Count)</td>
              {coverageCounts.map((item, i) => {
                const isTuesday = viewMode !== 'day' && columns[i] && new Date(columns[i]).getDay() === 2;
                return (
                  <td key={i} className={`text-center text-[10px] font-bold ${getStatusColor(item.count)} ${isTuesday && item.count === 0 ? '!bg-gray-200 dark:!bg-slate-700 text-gray-400' : ''}`}>
                    {item.count}
                  </td>
                );
              })}
            </tr>

            {/* Employee Rows (Sorted & Compact) */}
            {employees
              .filter(e => e.role !== 'admin' && e.role !== 'display' && !isEmployeeDisabled(e, selectedDate))
              .sort((a, b) => {
                const aEntries = getEntriesForDay(a.id, selectedDate);
                const bEntries = getEntriesForDay(b.id, selectedDate);
                const aActive = aEntries.length > 0;
                const bActive = bEntries.length > 0;
                if (aActive && !bActive) return -1;
                if (!aActive && bActive) return 1;
                return a.name.localeCompare(b.name);
              })
              .map(emp => {
                const dayEntries = getEntriesForDay(emp.id, selectedDate);
                const isActive = dayEntries.length > 0;
                return (
                  <tr key={emp.id} className={`border-b border-gray-50 dark:border-zinc-800/50 transition-colors ${isActive ? 'bg-blue-50/10 dark:bg-accent-mint/5' : 'hover:bg-gray-50 dark:hover:bg-zinc-800/30'}`}>
                    <td className={`p-2 font-medium sticky left-0 z-10 border-r border-gray-100 dark:border-zinc-800 shadow-[4px_0_8px_-2px_rgba(0,0,0,0.05)] text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[150px] ${isActive ? 'text-accent-mint dark:text-accent-mint bg-white dark:bg-zinc-900' : 'text-gray-700 dark:text-slate-300 bg-white dark:bg-zinc-900'}`}>
                      {emp.name}
                    </td>
                    {columns.map((col, i) => {
                      const isTuesday = viewMode !== 'day' && new Date(col).getDay() === 2;
                      return (
                        <td key={i} className={`p-0 h-8 align-middle ${(viewMode === 'week' && (i === 5 || i === 6)) ? 'bg-gray-50/30 dark:bg-slate-800/30' : ''} ${isTuesday ? '!bg-gray-50/50 dark:!bg-slate-800/20' : ''}`}>
                          {renderCell(emp.id, col)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap justify-end gap-4 text-xs text-gray-500 dark:text-slate-400 pt-4 border-t border-gray-100 dark:border-slate-700">
        <span className="font-bold mr-2">{t('coverageLegend')}</span>
        <div className="flex items-center"><div className="w-3 h-3 bg-red-500 mr-1.5 rounded-full shadow-sm"></div> 0</div>
        <div className="flex items-center"><div className="w-3 h-3 bg-yellow-400 mr-1.5 rounded-full shadow-sm"></div> 1</div>
        <div className="flex items-center"><div className="w-3 h-3 bg-green-500 mr-1.5 rounded-full shadow-sm"></div> 2</div>
        <div className="flex items-center"><div className="w-3 h-3 bg-purple-600 mr-1.5 rounded-full shadow-sm"></div> 3+</div>
      </div>
    </div>
  );
};


const BulkPrintView = ({ data, month, onDone, companyAddress, LOGO_URL, t, formatHours }) => {
  // Remove auto-print to fix mobile issues
  // Instead, show a header with Print/Close buttons

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Header for Manual Control */}
      <div className="fixed top-0 left-0 right-0 bg-white border-b p-4 flex justify-between items-center shadow-sm z-50 print:hidden text-black">
        <h2 className="font-bold text-lg">Stundenzettel: {month}</h2>
        <div className="flex gap-2">
          <Button
            variant="primary"
            onClick={() => window.print()}
          >
            <Printer className="w-4 h-4 mr-2" />
            {t('print')}
          </Button>
          <Button
            variant="secondary"
            onClick={onDone}
          >
            <X className="w-4 h-4 mr-2" />
            {t('close')}
          </Button>
        </div>
      </div>

      {/* Content with padding for header */}
      <div className="printable-bulk-container pt-20">
        {data.map(({ employee, userEntries, stats }) => (
          <div key={employee.id} className="page-break">
            <PrintableReport
              employee={employee}
              entries={sortEntries(userEntries)}
              month={month}
              companyAddress={companyAddress}
              LOGO_URL={LOGO_URL}
              t={t}
              formatHours={formatHours}
              stats={stats}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const TimeSelect = ({ value, onChange, className }) => {
  const [h, m] = (value || '00:00').split(':');

  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = ['00', '15', '30', '45'];

  return (
    <div className={`flex gap-1 ${className}`}>
      <select
        value={h}
        onChange={e => onChange(`${e.target.value}:${m}`)}
        className="w-full p-2 border dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 dark:text-white outline-none focus:border-accent-mint appearance-none text-center font-mono"
      >
        {hours.map(hour => (
          <option key={hour} value={hour}>{hour}</option>
        ))}
      </select>
      <span className="self-center text-gray-500 dark:text-slate-400 font-bold">:</span>
      <select
        value={m}
        onChange={e => onChange(`${h}:${e.target.value}`)}
        className="w-full p-2 border dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 dark:text-white outline-none focus:border-accent-mint appearance-none text-center font-mono"
      >
        {minutes.map(min => (
          <option key={min} value={min}>{min}</option>
        ))}
      </select>
    </div>
  );
};

const EditModal = ({ editingEntry, setEditingEntry, handleUpdateEntry, t, isRTL, employees }) => {
  if (!editingEntry) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white">{editingEntry.id ? t('editEntry') : (t('addEntry') || 'Eintrag hinzufügen')}</h3>
          <Button variant="ghost" size="iconSm" onClick={() => setEditingEntry(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="w-5 h-5" /></Button>
        </div>
        <div className="space-y-3">
          {!editingEntry.id && employees && (
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-slate-400 block mb-1">{t('employee') || 'Mitarbeiter'}</label>
              <select
                value={editingEntry.userId || ''}
                onChange={(e) => {
                  const selectedEmp = employees.find(emp => emp.id === e.target.value);
                  setEditingEntry({ ...editingEntry, userId: e.target.value, userName: selectedEmp ? selectedEmp.name : '' });
                }}
                className="w-full p-2 border dark:border-zinc-700 rounded-lg outline-none focus:border-accent-mint text-left bg-white dark:bg-zinc-800 dark:text-white"
              >
                <option value="" disabled>{t('selectEmployee') || 'Mitarbeiter wählen'}</option>
                {employees.filter(e => e.role !== 'admin' && e.role !== 'display' && e.approved !== false).map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.name}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-slate-400 block mb-1">{t('type') || 'Typ'}</label>
            <select
              value={editingEntry.type || 'WORK'}
              onChange={(e) => setEditingEntry({ ...editingEntry, type: e.target.value })}
              className={`w-full p-2 border dark:border-zinc-700 rounded-lg outline-none font-bold ${editingEntry.type === 'SICK' ? 'bg-red-50 text-red-700 dark:bg-red-900/30' : editingEntry.type === 'VACATION' ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30' : 'bg-white dark:bg-zinc-800 dark:text-white'}`}
            >
              <option value="WORK">🏢 {t('work') || 'Arbeit'}</option>
              <option value="VACATION">🏖️ {t('vacation') || 'Urlaub'}</option>
              <option value="SICK">🤒 {t('sick') || 'Krankheit'}</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-slate-400 block mb-1">{t('dateLabel')}</label>
            <input dir="ltr" type="date" value={editingEntry.date} onChange={(e) => setEditingEntry({ ...editingEntry, date: e.target.value })} className="w-full p-2 border dark:border-zinc-700 rounded-lg outline-none focus:border-accent-mint text-left bg-white dark:bg-zinc-800 dark:text-white" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {/* Force 30min steps and blur round */}
            <div><label className="text-xs font-medium text-gray-500 dark:text-slate-400 block mb-1">{t('startLabel')}</label>
              <TimeSelect value={editingEntry.startTime} onChange={(val) => setEditingEntry({ ...editingEntry, startTime: val })} />
            </div>
            <div><label className="text-xs font-medium text-gray-500 dark:text-slate-400 block mb-1">{t('endLabel')}</label>
              <TimeSelect value={editingEntry.endTime} onChange={(val) => setEditingEntry({ ...editingEntry, endTime: val })} />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-500 dark:text-slate-400 block mb-2">{t('breaks') || 'Pausen'}</label>
              {(editingEntry.breaks || (editingEntry.breakStartTime ? [{ start: editingEntry.breakStartTime, end: editingEntry.breakEndTime }] : [])).map((b, i, arr) => (
                <div key={i} className="flex gap-2 mb-2 items-center">
                  <div className="flex-1">
                    <TimeSelect value={b.start} onChange={(val) => {
                      const newBreaks = [...(editingEntry.breaks || (editingEntry.breakStartTime ? [{ start: editingEntry.breakStartTime, end: editingEntry.breakEndTime }] : []))];
                      newBreaks[i].start = val;
                      setEditingEntry({ ...editingEntry, breaks: newBreaks, breakStartTime: newBreaks[0]?.start, breakEndTime: newBreaks[0]?.end });
                    }} />
                  </div>
                  <span className="text-gray-400 self-center">-</span>
                  <div className="flex-1">
                    <TimeSelect value={b.end} onChange={(val) => {
                      const newBreaks = [...(editingEntry.breaks || (editingEntry.breakStartTime ? [{ start: editingEntry.breakStartTime, end: editingEntry.breakEndTime }] : []))];
                      newBreaks[i].end = val;
                      setEditingEntry({ ...editingEntry, breaks: newBreaks, breakStartTime: newBreaks[0]?.start, breakEndTime: newBreaks[0]?.end });
                    }} />
                  </div>
                  <Button variant="ghost" size="iconSm" onClick={() => {
                    const newBreaks = [...(editingEntry.breaks || (editingEntry.breakStartTime ? [{ start: editingEntry.breakStartTime, end: editingEntry.breakEndTime }] : []))];
                    newBreaks.splice(i, 1);
                    setEditingEntry({ ...editingEntry, breaks: newBreaks });
                  }} className="text-red-500 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
              <Button variant="link" onClick={() => {
                const newBreaks = [...(editingEntry.breaks || (editingEntry.breakStartTime ? [{ start: editingEntry.breakStartTime, end: editingEntry.breakEndTime }] : []))];
                newBreaks.push({ start: '', end: '' });
                setEditingEntry({ ...editingEntry, breaks: newBreaks });
              }} className="text-sm flex items-center mt-1">
                <Plus className="w-3 h-3 mr-1" /> {t('addBreak') || 'Pause hinzufügen'}
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-6 flex space-x-3">
          <Button variant="secondary" onClick={() => setEditingEntry(null)} className="flex-1 font-medium">{t('cancel')}</Button>
          <Button variant="primary" onClick={handleUpdateEntry} className="flex-1 font-bold shadow-sm">{t('saveButton')}</Button>
        </div>
      </div>
    </div>
  );
};

const DeactivateEmployeeModal = ({ deactivatingEmployee, setDeactivatingEmployee, deactivateDate, setDeactivateDate, handleDeactivateEmployee, t, isRTL }) => {
  if (!deactivatingEmployee) return null;
  // Default to tomorrow if not set
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl p-6 w-full max-w-md border border-orange-200 dark:border-orange-800/50">
        <div className="flex justify-between items-center mb-5">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <UserX className="w-5 h-5 text-orange-600 dark:text-orange-400" />
            </div>
            <h3 className="text-lg font-bold text-gray-800 dark:text-white">{t('confirmDeactivate')}</h3>
          </div>
          <button onClick={() => setDeactivatingEmployee(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full p-1 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-5">
          <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700/50 rounded-xl p-3 mb-4">
            <p className="text-sm font-semibold text-orange-800 dark:text-orange-300">{deactivatingEmployee.name}</p>
            <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">@{deactivatingEmployee.username || '-'}</p>
          </div>

          <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
            {t('deactivateFromDate')}
          </label>
          <input
            type="date"
            value={deactivateDate || tomorrowStr}
            onChange={(e) => setDeactivateDate(e.target.value)}
            className="w-full p-3 border border-gray-300 dark:border-zinc-600 rounded-xl bg-white dark:bg-zinc-800 dark:text-white outline-none focus:border-orange-400 dark:focus:border-orange-500 text-sm font-mono"
          />

          <div className="mt-3 flex items-start gap-2 text-xs text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-zinc-800/50 rounded-lg p-3">
            <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
            <span>{t('deactivateWarning')}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={() => setDeactivatingEmployee(null)}
            className="flex-1 py-2.5 px-4 rounded-xl border border-gray-200 dark:border-zinc-700 text-gray-600 dark:text-slate-300 font-medium text-sm hover:bg-gray-50 dark:hover:bg-zinc-800 transition-colors"
          >
            {t('cancel')}
          </button>
          <button
            onClick={() => {
              if (!deactivateDate) setDeactivateDate(tomorrowStr);
              handleDeactivateEmployee();
            }}
            className="flex-1 py-2.5 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm transition-colors shadow-sm flex items-center justify-center gap-2"
          >
            <UserX className="w-4 h-4" />
            {t('deactivateEmployee')}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteEmployeeModal = ({ employeeToDelete, setEmployeeToDelete, handleDeleteEmployee, t, isRTL }) => {

  if (!employeeToDelete) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 print:hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl p-6 w-full max-w-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white">{t('confirmDeleteEmployee')}</h3>
          <Button variant="ghost" size="iconSm" onClick={() => setEmployeeToDelete(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="w-5 h-5" /></Button>
        </div>
        <div className="mb-6">
          <p className="text-sm text-gray-600 dark:text-slate-300 mb-2">{t('confirmDeleteEmployeeText')}</p>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mt-4">
            <p className="text-sm font-semibold text-red-800 dark:text-red-300">{employeeToDelete.name}</p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button variant="secondary" onClick={() => setEmployeeToDelete(null)} className="flex-1 font-medium">{t('cancel')}</Button>
          <Button variant="destructive" onClick={handleDeleteEmployee} className="flex-1 font-bold shadow-sm">{t('delete')}</Button>
        </div>
      </div>
    </div>
  );
};

const ShiftModal = ({ show, onClose, editingShift, employees, onSave, t, isRTL }) => {
  const [formData, setFormData] = React.useState({
    employeeId: '',
    date: '',
    startTime: '08:00',
    endTime: '16:00',
    shiftType: '',
    type: 'WORK',
    notes: ''
  });

  React.useEffect(() => {
    if (!show) return;

    if (editingShift) {
      setFormData({
        employeeId: editingShift.employeeId,
        date: editingShift.date,
        startTime: editingShift.startTime,
        endTime: editingShift.endTime,
        shiftType: editingShift.shiftType || '',
        type: editingShift.type || 'WORK',
        notes: editingShift.notes || ''
      });
    } else {
      setFormData({
        employeeId: employees.filter(e => e.approved !== false && !isEmployeeDisabled(e, new Date().toISOString().split('T')[0]))[0]?.id || '',
        date: new Date().toISOString().split('T')[0],
        startTime: '08:00',
        endTime: '16:00',
        shiftType: '',
        type: 'WORK',
        notes: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingShift, show]);

  const handleSubmit = () => {
    if (!formData.employeeId || !formData.date || !formData.startTime || !formData.endTime) {
      return;
    }
    onSave(formData);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800 dark:text-white">
            {editingShift ? t('editShift') : t('addShift')}
          </h3>
          <Button variant="ghost" size="iconSm" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </Button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300 block mb-1">
              {t('selectEmployee')}
            </label>
            <select
              value={formData.employeeId}
              onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
              className="w-full p-2 border dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 dark:text-white"
              disabled={!!editingShift}
            >
              {employees.filter(e => e.approved !== false && !isEmployeeDisabled(e, formData.date || new Date().toISOString().split('T')[0])).map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300 block mb-1">
              {t('dateLabel')}
            </label>
            <input
              dir="ltr"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full p-2 border dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 dark:text-white"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300 block mb-1">
                {t('type') || 'Typ'}
              </label>
              <select
                value={formData.type || 'WORK'}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className={`w-full p-2 border dark:border-slate-600 rounded-lg dark:text-white font-bold ${formData.type === 'SICK' ? 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400' : formData.type === 'VACATION' ? 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : formData.type === 'TIME_TRACKING' ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-white dark:bg-slate-700'}`}
              >
                <option value="WORK">🏢 {t('work') || 'Arbeit'}</option>
                <option value="VACATION">🏖️ {t('vacation') || 'Urlaub'}</option>
                <option value="SICK">🤒 {t('sick') || 'Krankheit'}</option>
                <option value="TIME_TRACKING">⏱️ {t('recordTime') || 'Zeit erfassen'}</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300 block mb-1">
                {t('startLabel')}
              </label>
              <TimeSelect
                value={formData.startTime}
                onChange={(val) => setFormData({ ...formData, startTime: val })}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-slate-300 block mb-1">
                {t('endLabel')}
              </label>
              <TimeSelect
                value={formData.endTime}
                onChange={(val) => setFormData({ ...formData, endTime: val })}
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300 block mb-1">
              {t('shiftType')} ({t('optional') || 'optional'})
            </label>
            <input
              type="text"
              value={formData.shiftType}
              onChange={(e) => setFormData({ ...formData, shiftType: e.target.value })}
              className="w-full p-2 border dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 dark:text-white"
              placeholder="z.B. Frühschicht"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300 block mb-1">
              {t('shiftNotes')} ({t('optional') || 'optional'})
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full p-2 border dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 dark:text-white"
              rows="2"
              placeholder={t('notes') || 'Notizen...'}
            />
          </div>
        </div>
        <div className="mt-6 flex space-x-3">
          <Button
            variant="secondary"
            onClick={onClose}
            className="flex-1 font-medium"
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="flex-1 font-bold shadow-sm"
          >
            {t('saveButton')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const SettlementModal = ({ show, onClose, employee, monthlyClosings, timeEntries, onSave, t, isRTL, formatHours }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().substring(0, 7));
  const [paidHoursInput, setPaidHoursInput] = useState('');

  // Calculate stats for the selected month to show preview
  const stats = React.useMemo(() => {
    if (!employee) return null;
    // We reuse the calculation logic but strictly for this month
    // calculateMonthlyBalances expects array of closings
    // We want to see what happens IF we save this input, so we might need to mock the closing array?
    // Actually, just calculating "Current Status" vs "Projected Status" is enough.

    // 1. Get worked hours
    const monthEntries = timeEntries.filter(e => e.userId === employee.id && e.monthKey === selectedMonth);
    // eslint-disable-next-line no-unused-vars
    const worked = monthEntries.reduce((sum, e) => sum + (Number(e.duration) || 0), 0);

    // 2. Get Previous Carryover (Balance of previous month)
    // To do this accurately, we need the balance map up to this month.
    // Let's use the main calculation function for the WHOLE history up to selectedMonth
    const balances = calculateMonthlyBalances(employee.id, timeEntries, employee, selectedMonth, monthlyClosings, employee.employmentType);
    const currentMonthStats = balances[selectedMonth] || { worked: 0, carryOverBefore: 0, totalAvailable: 0, paidOrDue: getEffectiveMaxHours(employee, selectedMonth), balance: 0 };

    // 3. Projected
    const inputVal = parseFloat(paidHoursInput);
    const isValidInput = !isNaN(inputVal);
    const projectedPaid = isValidInput ? inputVal : currentMonthStats.paidOrDue; // or maxHours if nothing entered? 
    // If input is empty, and we ALREADY have a closing, show that. If input is empty and no closing, show maxHours.
    // But here we want to enable editing.

    const projectedBalance = currentMonthStats.totalAvailable - projectedPaid;

    return {
      ...currentMonthStats,
      projectedPaid,
      projectedBalance,
      isValidInput
    };
  }, [employee, monthlyClosings, timeEntries, selectedMonth, paidHoursInput]);

  // Load existing closing when month changes
  useEffect(() => {
    if (show && employee) {
      const existing = monthlyClosings.find(c => c.userId === employee.id && c.monthKey === selectedMonth);
      if (existing) {
        setPaidHoursInput(existing.paidHours);
      } else {
        setPaidHoursInput(''); // Clear input if no manual entry exists (default is MaxHours logic)
      }
    }
  }, [show, employee, selectedMonth, monthlyClosings]);

  if (!show || !employee) return null;

  const targetHours = getEffectiveMaxHours(employee, selectedMonth);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-white dark:bg-zinc-900 rounded-xl shadow-xl p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
            <span className="bg-green-100 text-green-600 p-2 rounded-lg mr-3">💰</span>
            {t('settlements') || 'Abrechnung'}
          </h3>
          <Button variant="ghost" size="iconSm" onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="space-y-6">
          {/* Employee Info */}
          <div className="flex items-center justify-between bg-gray-50 dark:bg-slate-700/50 p-3 rounded-lg">
            <span className="font-medium text-gray-700 dark:text-slate-300">{employee.name}</span>
            <span className="text-sm text-gray-500 dark:text-slate-400">@{employee.username}</span>
          </div>

          {/* Month Selection */}
          <div>
            <label className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-1 block">
              {t('selectMonth') || 'Monat wählen'}
            </label>
            <input
              type="month"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-full p-2 border dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 dark:text-white font-medium shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          {/* Calculation Preview Box */}
          <div className="border dark:border-slate-700 rounded-lg overflow-hidden">
            <div className="bg-gray-50 dark:bg-slate-700/50 p-3 border-b dark:border-slate-700 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600 dark:text-slate-300">Übertrag aus Vormonat</span>
              <span className={`font-mono font-bold ${stats?.carryOverBefore < 0 ? 'text-red-500' : 'text-green-500'}`}>
                {formatHours(stats?.carryOverBefore)}
              </span>
            </div>
            <div className="bg-gray-50 dark:bg-slate-700/50 p-3 border-b dark:border-slate-700 flex justify-between items-center">
              <span className="text-sm font-medium text-gray-600 dark:text-slate-300">Gearbeitet ({selectedMonth})</span>
              <span className="font-mono font-bold text-accent-mint dark:text-accent-mint">
                + {formatHours(stats?.worked)}
              </span>
            </div>
            <div className="bg-blue-50 dark:bg-accent-mint/20 p-3 border-b dark:border-slate-700 flex justify-between items-center">
              <span className="text-sm font-bold text-gray-700 dark:text-slate-200">Gesamt Verfügbar</span>
              <span className="font-mono font-bold text-gray-800 dark:text-white">
                = {formatHours(stats?.totalAvailable)}
              </span>
            </div>

            {/* INPUT AREA */}
            <div className="p-4 bg-white dark:bg-slate-800">
              <label className="text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">
                {t('paidHours') || 'Bezahlte Stunden'} (Soll: {targetHours}h)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={paidHoursInput}
                  onChange={(e) => setPaidHoursInput(e.target.value)}
                  placeholder={targetHours}
                  className="flex-1 p-2 text-lg font-bold text-right border dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-900 dark:text-white focus:ring-2 focus:ring-green-500 outline-none"
                />
                <span className="text-gray-500 font-medium">h</span>
              </div>
              <p className="text-xs text-gray-400 mt-1 text-right">
                {paidHoursInput === '' ? 'Kein manueller Eintrag (Standard: Vertragsstunden)' : 'Manueller Eintrag aktiv'}
              </p>
            </div>

            {/* RESULT PREVIEW */}
            <div className="bg-green-50 dark:bg-green-900/20 p-3 border-t dark:border-slate-700 flex justify-between items-center">
              <span className="text-sm font-bold text-green-800 dark:text-green-300">Neuer Übertrag (Nächster Mon.)</span>
              <span className={`font-mono font-bold text-lg ${stats?.projectedBalance < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {formatHours(stats?.projectedBalance)}
              </span>
            </div>
          </div>

          <div className="flex space-x-3 pt-2">
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex-1 py-2.5 font-medium transition-colors"
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => onSave(employee.id, selectedMonth, paidHoursInput)}
              className="flex-1 py-2.5 font-bold shadow-sm transition-colors flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {t('saveButton')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

};

const AppHeader = ({ title, subtitle, icon: Icon, colorClass = "blue", isRTL, lang, setLang, showPasswordChange, setShowPasswordChange, newPassword, setNewPassword, handleChangePassword, onLogout, t, darkMode, toggleDarkMode, children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // surface-dark usage for header
  return (
    <header className={`bg-white dark:bg-black shadow-sm sticky top-0 z-20 border-b border-${colorClass}-100 dark:border-zinc-800 print:hidden transition-colors duration-300`}>
      <div className="flex justify-between items-center px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center space-x-3 overflow-hidden">
          <img src={LOGO_URL} alt="Logo" className="h-8 sm:h-10 object-contain flex-shrink-0" />
          <div className={`flex items-center space-x-2 px-2 sm:px-4 ${isRTL ? 'border-r' : 'border-l'} border-gray-200 dark:border-slate-600 min-w-0`}>
            <div className={`p-1.5 sm:p-2 rounded-full bg-${colorClass}-100 dark:bg-zinc-800 hidden sm:block flex-shrink-0`}><Icon className={`w-4 h-4 sm:w-5 sm:h-5 text-${colorClass}-600 dark:text-${colorClass}-400`} /></div>
            <div className="min-w-0">
              <h2 className="font-bold text-gray-800 dark:text-white text-sm sm:text-base truncate">{title}</h2>
              <p className="text-xs text-gray-500 dark:text-slate-400 truncate hidden xs:block">{subtitle}</p>
            </div>
          </div>
        </div>

        <div className="hidden md:flex items-center space-x-3 flex-shrink-0">
          <Button variant="ghost" size="icon" onClick={() => window.location.reload()} className="hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-slate-500" title={t('refresh')}>
            <RefreshCw className="w-5 h-5" />
          </Button>
          <DarkModeToggle darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          <LanguageSwitcher lang={lang} setLang={setLang} />
          <Button variant="ghost" size="icon" onClick={() => setShowPasswordChange(!showPasswordChange)} className="hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-slate-500"><Key className="w-5 h-5" /></Button>
          <Button variant="ghost" size="icon" onClick={onLogout} className="hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 dark:text-slate-500"><LogOut className="w-5 h-5" /></Button>
        </div>

        <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden text-gray-600 dark:text-slate-300">
          <Menu className="w-6 h-6" />
        </Button>
      </div>

      {isMenuOpen && (
        <div className="md:hidden mt-4 pt-4 border-t border-gray-100 dark:border-slate-700 flex flex-col space-y-4 animate-in slide-in-from-top-2">
          <Button variant="ghost" fullWidth onClick={() => window.location.reload()} className="flex items-center justify-between text-sm text-gray-600 dark:text-slate-400 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 h-auto">
            <span>{t('refresh')}</span>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <div className="flex justify-between items-center px-2">
            <span className="text-sm text-gray-600 dark:text-slate-400 font-medium">{t('settingsDesign')}</span>
            <DarkModeToggle darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          </div>
          <div className="flex justify-between items-center px-2">
            <span className="text-sm text-gray-600 dark:text-slate-400 font-medium">{t('settingsLang')}</span>
            <LanguageSwitcher lang={lang} setLang={setLang} />
          </div>
          <Button variant="ghost" fullWidth onClick={() => { setShowPasswordChange(!showPasswordChange); setIsMenuOpen(false); }} className="flex items-center justify-between text-sm text-gray-600 dark:text-slate-400 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 h-auto">
            <span>{t('settingsPass')}</span>
            <Key className="w-4 h-4" />
          </Button>
          <Button variant="ghost" fullWidth onClick={onLogout} className="flex items-center justify-between text-sm text-red-600 dark:text-red-400 p-2 rounded hover:bg-red-50 dark:hover:bg-red-900/20 h-auto">
            <span>{t('logout')}</span>
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      )}

      {showPasswordChange && (
        <div className={`absolute top-16 bg-white dark:bg-zinc-900 shadow-xl border border-gray-200 dark:border-zinc-700 rounded-xl p-4 w-64 z-30 ${isRTL ? 'left-4' : 'right-4'}`}>
          <h4 className="text-sm font-bold text-gray-700 dark:text-slate-200 mb-2">{t('newPasswordTitle')}</h4>
          <input
            dir="ltr"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="w-full p-2 border dark:border-slate-600 rounded text-sm mb-2 text-left bg-white dark:bg-slate-700 dark:text-white"
            placeholder={t('newPasswordPlaceholder')}
          />
          <div className="flex space-x-2">
            <Button variant="primary" size="sm" onClick={handleChangePassword} disabled={!newPassword} className="flex-1 text-xs py-2 rounded">{t('saveButton')}</Button>
            <Button variant="secondary" size="iconSm" onClick={() => setShowPasswordChange(false)} className="rounded"><X className="w-4 h-4" /></Button>
          </div>
        </div>
      )}
      {children && (
        <div className="px-4 pb-2 -mt-2 sm:px-6">
          {children}
        </div>
      )}
    </header>
  );
};



// --- MAIN APP ---
export default function App() {
  const [user, setUser] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [lang, setLang] = useState('de');
  const [showImpressum, setShowImpressum] = useState(false);
  const [showDatenschutz, setShowDatenschutz] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [loginName, setLoginName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  // adminCodeInput removed
  const [authError, setAuthError] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [entryDate, setEntryDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]); // New End Date State
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('16:00');
  const [breakStartTime, setBreakStartTime] = useState('');
  const [breakEndTime, setBreakEndTime] = useState('');
  const [breaks, setBreaks] = useState([]);
  const [entryType, setEntryType] = useState('WORK'); // New State for Type // Array of {start, end}
  const [leaveDuration, setLeaveDuration] = useState('FULL'); // 'FULL' or 'HALF'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [printReportData, setPrintReportData] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [editingNameId, setEditingNameId] = useState(null);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newUsername, setNewUsername] = useState('');
  const [filterMode, setFilterMode] = useState('month');
  const [notification, setNotification] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [boardStatus, setBoardStatus] = useState({ offline: false, lastSync: null }); // Aushang (Rolle display)
  const [pendingLogin, setPendingLogin] = useState(null); // { username, pin } bis neuer PIN gesetzt ist
  const [upgradePassword, setUpgradePassword] = useState('');
  const [username, setUsername] = useState('');

  const [showTargetHoursModal, setShowTargetHoursModal] = useState(false);
  const [activeTargetHoursEmployee, setActiveTargetHoursEmployee] = useState(null);

  // NEW: Employee View Month State
  const [employeeViewMonth, setEmployeeViewMonth] = useState(getCurrentMonthKey());
  const [adminSelectedMonth, setAdminSelectedMonth] = useState(getCurrentMonthKey());
  const [adminSelectedEmployee, setAdminSelectedEmployee] = useState('');
  const [bulkPrintData, setBulkPrintData] = useState(null);
  const printRef = React.useRef();
  const [companyAddress] = useState('ell.in Grill & Pizzeria, Mühlenstr. 2, 41352 Korschenbroich');

  // Shift Scheduling State
  const [shifts, setShifts] = useState([]);
  const [activeAdminTab, setActiveAdminTab] = useState('dashboard'); // 'dashboard' | 'employees' | 'entries' | 'schedule'

  const [editingShift, setEditingShift] = useState(null);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [activeEmployeeTab, setActiveEmployeeTab] = useState('timeTracking'); // 'timeTracking' | 'myShifts'
  const [monthlyClosings, setMonthlyClosings] = useState([]);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [activeSettlementEmployee, setActiveSettlementEmployee] = useState(null);
  // Deactivation modal state
  const [deactivatingEmployee, setDeactivatingEmployee] = useState(null);
  const [deactivateDate, setDeactivateDate] = useState('');
  // Employee card UI state (expand/collapse + overflow menu)
  const [expandedEmpIds, setExpandedEmpIds] = useState(() => new Set());
  const [openMenuEmpId, setOpenMenuEmpId] = useState(null);
  const toggleEmpExpanded = (id) => setExpandedEmpIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    if (saved !== null) return JSON.parse(saved);
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const toggleDarkMode = () => setDarkMode(!darkMode);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    if (printReportData) {
      const timer = setTimeout(() => {
        window.print();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [printReportData]);

  // --- PUSH NOTIFICATIONS ---
  useEffect(() => {
    const setupNotifications = async () => {
      if (!currentProfile || currentProfile.role === 'display') return;

      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          // NOTE: Generate a VAPID Key in Firebase Console -> Project Settings -> Cloud Messaging -> Web Push Certificates
          // and replace the string below.
          const token = await getToken(messaging, {
            vapidKey: "BBd--IIOkaqMYrmavnvdzQsXv43NB9SNAO-E_hvNKz7tO2Bzp9GtvI9-oJWH7MGGaezf68oRE78M4fEsBYQ03NI"
          }).catch((err) => {
            console.error("Error getting token (check VAPID key):", err);
            return null;
          });

          if (token) {
            console.log("FCM Token:", token);
            // Save token to employee profile
            await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', currentProfile.id), {
              fcmTokens: { [token]: true }
            }, { merge: true });
          }
        }
      } catch (err) {
        console.error("Notification setup error:", err);
      }
    };

    if (currentProfile) {
      setupNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProfile?.id]);

  const t = (key) => translations[lang][key] || key;
  const isRTL = lang === 'ar';

  // --- HELPER ---
  const showToast = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const isValidPin = (pin) => /^\d{6}$/.test(pin);

  // --- SERVERSEITIGER LOGIN (Cloud Functions) ---
  const callFn = async (name, data) => (await httpsCallable(functions, name)(data)).data;

  const fnErrorMessage = (e) => {
    const reason = e && e.details && e.details.reason;
    if (reason === 'pending') return t('accountPending') || 'Konto wartet auf Freigabe.';
    if (reason === 'disabled') return t('accountDisabled') || 'Dieser Account wurde deaktiviert.';
    if (reason === 'nameTaken') return t('nameTaken');
    if (reason === 'usernameTaken') return t('usernameTaken');
    if (reason === 'pin') return t('pinError') || 'PIN muss 6 Zahlen haben.';
    if (reason === 'email') return t('emailError') || 'Ungültige E-Mail.';
    if (e && e.code === 'functions/internal') return t('saveError');
    return (e && e.message) || t('saveError');
  };

  const handleLogin = async () => {
    setAuthError('');
    if (!loginName.trim() || !loginPassword.trim()) return setAuthError(t('fillData'));
    setAuthBusy(true);
    try {
      const credentials = { username: loginName.trim(), pin: loginPassword.trim() };
      const res = await callFn('login', credentials);
      setLoginName(''); setLoginPassword('');
      if (!res.token && res.mustChangePin) {
        // Server gibt erst nach Vergabe eines neuen PINs eine Sitzung heraus
        setPendingLogin(credentials);
        return;
      }
      await signInWithCustomToken(auth, res.token);
      showToast(t('toastWelcome'), 'success');
    } catch (e) {
      console.error('Login fehlgeschlagen:', e);
      setAuthError(fnErrorMessage(e));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleRegister = async () => {
    setAuthError('');
    if (!firstName.trim() || !lastName.trim() || !username.trim() || !loginPassword.trim()) return setAuthError(t('fillData'));
    if (!isValidPin(loginPassword.trim())) return setAuthError(t('pinError') || 'PIN muss 6 Zahlen haben.');
    if (loginEmail && !loginEmail.includes('@')) return setAuthError(t('emailError') || 'Ungültige E-Mail.');

    setAuthBusy(true);
    try {
      const res = await callFn('register', {
        firstName: firstName.trim(), lastName: lastName.trim(), username: username.trim(),
        email: loginEmail.trim(), pin: loginPassword.trim()
      });
      if (res.token) {
        await signInWithCustomToken(auth, res.token);
        showToast(t('toastWelcome'), 'success');
      } else {
        showToast(t('regSuccessPending') || 'Registration successful. Please wait for admin approval.', 'success');
        setAuthMode('login');
      }
      setLoginName(''); setFirstName(''); setLastName(''); setUsername(''); setLoginPassword(''); setLoginEmail('');
    } catch (e) {
      console.error('Registrierung fehlgeschlagen:', e);
      setAuthError(fnErrorMessage(e));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleUpgradePassword = async () => {
    if (!upgradePassword.trim()) return;
    if (!isValidPin(upgradePassword.trim())) {
      alert(t('pinError') || 'PIN muss 6 Zahlen haben.');
      return;
    }
    setAuthBusy(true);
    try {
      if (pendingLogin) {
        const res = await callFn('login', { ...pendingLogin, newPin: upgradePassword.trim() });
        await signInWithCustomToken(auth, res.token);
        setPendingLogin(null);
      } else {
        // Bereits angemeldet (z. B. nach Admin-Reset): mustChangePin kommt per Snapshot zurück
        await callFn('changePin', { newPin: upgradePassword.trim() });
      }
      setUpgradePassword('');
      showToast(t('passChanged'), 'success');
    } catch (e) {
      console.error(e);
      showToast(fnErrorMessage(e), 'error');
    } finally {
      setAuthBusy(false);
    }
  };

  const handleExportData = () => {
    const data = {
      employees,
      timeEntries,
      // If shifts are not defined, fallback to empty array to prevent crash
      shifts: typeof shifts !== 'undefined' ? shifts : [],
      exportDate: new Date().toISOString()
    };
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `zeiterfassung_backup_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  };

  const handleLogout = async () => {
    setPendingLogin(null);
    setUpgradePassword('');
    setEditingEntry(null);
    setPrintReportData(null);
    setActiveAdminTab('dashboard');
    setActiveEmployeeTab('timeTracking');
    try { await signOut(auth); } catch (e) { console.error('Logout:', e); }
    setLoginName('');
    setLoginPassword('');
  };

  const handleUpdateEntry = async () => {
    if (!editingEntry) return;

    // Check for overlapping entries (excluding the current entry being edited)
    const newStart = timeToDecimal(editingEntry.startTime);
    const newEnd = timeToDecimal(editingEntry.endTime);

    const overlappingEntry = timeEntries.find(entry => {
      // Skip the entry we're currently editing
      if (entry.id === editingEntry.id) return false;
      if (entry.userId !== editingEntry.userId || entry.date !== editingEntry.date) return false;

      const existingStart = timeToDecimal(entry.startTime);
      const existingEnd = timeToDecimal(entry.endTime);

      // Check if times overlap
      return (newStart < existingEnd && newEnd > existingStart);
    });

    if (overlappingEntry) {
      showToast(t('overlapError'), 'error');
      return;
    }

    try {
      const startDec = timeToDecimal(editingEntry.startTime);
      const endDec = timeToDecimal(editingEntry.endTime);
      const grossDuration = endDec - startDec;

      // Calculate Break Duration from Array or Legacy
      let breakDur = 0;
      let finalBreaks = editingEntry.breaks || [];

      // Migration: If no breaks array but legacy fields exist, create one
      if (finalBreaks.length === 0 && editingEntry.breakStartTime && editingEntry.breakEndTime) {
        finalBreaks = [{ start: editingEntry.breakStartTime, end: editingEntry.breakEndTime }];
      }

      if (finalBreaks.length > 0) {
        for (const b of finalBreaks) {
          const bs = timeToDecimal(b.start);
          const be = timeToDecimal(b.end);
          if (be <= bs) {
            showToast(t('negativeDuration') || 'Fehler: Pause Ende muss nach Pause Start sein.', 'error');
            return;
          }
          breakDur += (be - bs) * 60;
        }
      } else if (editingEntry.breakDuration) {
        // Fallback for manual duration input
        breakDur = Number(editingEntry.breakDuration);
      }

      // ArbZG Validation
      if (grossDuration > 6 && breakDur < 30) {
        if (!window.confirm(t('breakWarning6h') || 'Achtung: Bei über 6 Stunden Arbeit sind gesetzlich 30 Minuten Pause vorgeschrieben. Trotzdem speichern?')) {
          return;
        }
      }
      if (grossDuration > 9 && breakDur < 45) {
        if (!window.confirm(t('breakWarning9h') || 'Achtung: Bei über 9 Stunden Arbeit sind gesetzlich 45 Minuten Pause vorgeschrieben. Trotzdem speichern?')) {
          return;
        }
      }

      const netDuration = grossDuration - (breakDur / 60);

      if (netDuration <= 0) {
        showToast(t('negativeDuration') || 'Fehler: Arbeitszeit minus Pause ist negativ oder Null.', 'error');
        return;
      }

      const entryData = {
        userId: editingEntry.userId,
        userName: editingEntry.userName, // Update name in case changed? No, usually not.
        date: editingEntry.date,
        startTime: editingEntry.startTime,
        endTime: editingEntry.endTime,
        breaks: finalBreaks,
        breakStartTime: finalBreaks.length > 0 ? finalBreaks[0].start : null, // Legacy Sync
        breakEndTime: finalBreaks.length > 0 ? finalBreaks[0].end : null,     // Legacy Sync
        breakDuration: breakDur,
        duration: netDuration,
        type: editingEntry.type || 'WORK',
        monthKey: editingEntry.date.substring(0, 7),
      };

      if (editingEntry.id) {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'time_entries', editingEntry.id), entryData);
      } else {
        // Create new entry
        // NOTE: userId and userName are already in entryData but need ensuring
        await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'time_entries'), {
          ...entryData,
          createdAt: serverTimestamp()
        });

        // Update lastTimeEntry for the user
        try {
          await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', editingEntry.userId), {
            lastTimeEntry: serverTimestamp()
          });
        } catch (e) { console.error('Error updating lastTimeEntry:', e); }
      }

      setEditingEntry(null);
      showToast(t('saved'), 'success');
    } catch (e) { showToast(t('saveError'), 'error'); }
  };

  const handleUpdateName = async (empId) => {
    if (!newFirstName.trim() || !newLastName.trim() || !newUsername.trim()) {
      showToast(t('fillData'), 'error');
      return;
    }
    const fullName = `${newFirstName.trim()} ${newLastName.trim()}`;
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', empId), {
        name: fullName,
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        username: newUsername.trim()
      });
      setEmployees(prev => prev.map(e => e.id === empId ? { ...e, name: fullName, firstName: newFirstName.trim(), lastName: newLastName.trim(), username: newUsername.trim() } : e));
      setEditingNameId(null);
      setNewFirstName('');
      setNewLastName('');
      setNewUsername('');
      showToast(t('passChanged'), 'success');
    } catch (e) { console.error(e); showToast(t('saveError'), 'error'); }
  };

  const handleUpdateMaxHours = async (empId, newMax) => {
    const val = parseFloat(newMax);
    if (isNaN(val)) return;
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', empId), { maxHours: val });
      showToast(t('saved'), 'success');
    } catch (e) { showToast(t('saveError'), 'error'); }
  };

  const handleUpdateTargetHoursHistory = async (empId, newHistory) => {
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', empId), { targetHoursHistory: newHistory });
      showToast(t('saved'), 'success');
    } catch (e) { showToast(t('saveError'), 'error'); }
  };

  const handleUpdateEmail = async (empId, newEmail) => {
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', empId), { email: newEmail });
      showToast(t('saved'), 'success');
    } catch (e) { showToast(t('saveError'), 'error'); }
  };

  const handleUpdateColor = async (empId, newColor) => {
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', empId), { color: newColor });
    } catch (e) { console.error("Error updating color: ", e); showToast(t('saveError'), 'error'); }
  };

  const handleUpdateEmploymentType = async (empId, newType) => {
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', empId), { employmentType: newType });
    } catch (e) { console.error("Error updating type: ", e); showToast(t('saveError'), 'error'); }
  };

  const handleUpdateWeeklyHours = async (empId, newHours) => {
    const val = parseFloat(newHours);
    if (isNaN(val)) return;
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', empId), { weeklyHours: val });
    } catch (e) { console.error("Error updating weekly hours: ", e); showToast(t('saveError'), 'error'); }
  };

  const handleSaveSettlement = async (empId, monthKey, val) => {
    let paid = parseFloat(val);

    // If input is empty string, we might want to DELETE the entry to revert to default?
    // Or just save it as null? Let's check logic.
    // If user clears input, we should probably delete the custom settlement.

    if (val === '' || val === null) {
      const docId = `${empId}_${monthKey}`;
      try {
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'monthly_closings', docId));
        showToast('Standard-Berechnung wiederhergestellt', 'info');
        setShowSettlementModal(false);
      } catch (e) {
        showToast(t('saveError'), 'error');
      }
      return;
    }

    if (isNaN(paid)) {
      showToast('Ungültiger Wert', 'error');
      return;
    }

    const docId = `${empId}_${monthKey}`;
    try {
      await setDoc(doc(db, 'artifacts', appId, 'public', 'data', 'monthly_closings', docId), {
        userId: empId,
        monthKey: monthKey,
        paidHours: paid,
        updatedAt: serverTimestamp()
      }, { merge: true });
      showToast('Abrechnung gespeichert', 'success');
      setShowSettlementModal(false);
    } catch (e) {
      console.error(e);
      showToast(t('saveError'), 'error');
    }
  };



  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm(t('confirmDelete'))) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'time_entries', entryId));
      showToast(t('deleted'), 'success');
    } catch (e) { showToast(t('saveError'), 'error'); }
  };

  const handleApproveUser = async (emp) => {
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', emp.id), { approved: true });
      showToast(t('userApproved') || 'User approved', 'success');
    } catch (e) { showToast(t('saveError'), 'error'); }
  };

  const handleRejectUser = async (emp) => {
    if (!window.confirm(t('confirmReject') || 'Reject and delete this user?')) return;
    try {
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', emp.id));
      showToast(t('userRejected') || 'User rejected', 'success');
    } catch (e) { showToast(t('saveError'), 'error'); }
  };

  const handleDeactivateEmployee = async () => {
    if (!deactivatingEmployee || !deactivateDate) return;
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', deactivatingEmployee.id), {
        disabledFrom: deactivateDate
      });
      showToast(`${t('employeeDeactivated')} ${deactivateDate}`, 'success');
      setDeactivatingEmployee(null);
      setDeactivateDate('');
    } catch (e) { showToast(t('saveError'), 'error'); }
  };

  const handleReactivateEmployee = async (emp) => {
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', emp.id), {
        disabledFrom: null
      });
      showToast(t('employeeReactivated'), 'success');
    } catch (e) { showToast(t('saveError'), 'error'); }
  };

  const handleAddEntry = async () => {
    if (isSubmitting) return;

    const isLeave = entryType !== 'WORK';

    // Prevent future work entries
    if (!isLeave) {
      const today = new Date().toISOString().split('T')[0];
      if (entryDate > today) {
        showToast(t('futureWorkError'), 'error');
        return;
      }
    }

    // Logic for Date Range (Leave)
    if (isLeave) {
      const datesToProcess = [];
      const startD = new Date(entryDate);
      const endD = new Date(endDate || entryDate); // Fallback to entryDate if empty

      // Iterate from start to end
      for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
        // Skip Tuesday (Day 2)
        if (d.getDay() === 2) continue;
        datesToProcess.push(new Date(d));
      }

      if (datesToProcess.length === 0) {
        showToast(t('noDaysSelected') || 'Keine Tage ausgewählt (Dienstag ist geschlossen).', 'warning');
        return;
      }

      setIsSubmitting(true);
      let createdCount = 0;

      for (const dateObj of datesToProcess) {
        const currentDateStr = dateObj.toISOString().split('T')[0];

        // Basic Overlap Check (Simplified for bulk)
        const overlap = timeEntries.find(e =>
          e.userId === currentProfile.id &&
          e.date === currentDateStr
        );

        if (overlap) continue; // Skip if entry exists

        const calculatedDuration = calculateAbsenceHours({
          employee: currentProfile,
          date: currentDateStr,
          absenceType: entryType,
          shifts: shifts || [],
          leaveDuration: leaveDuration
        });

        const finalStart = '08:00';
        const finalEnd = '16:00';

        const entryData = {
          userId: currentProfile.id,
          userName: currentProfile.name,
          date: currentDateStr,
          startTime: finalStart,
          endTime: finalEnd,
          breaks: [],
          breakStartTime: null,
          breakEndTime: null,
          breakDuration: 0,
          duration: calculatedDuration,
          type: entryType,
          monthKey: currentDateStr.substring(0, 7),
          ip: 'bulk-entry',
          createdAt: serverTimestamp()
        };

        try {
          await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'time_entries'), entryData);
          createdCount++;
        } catch (e) {
          console.error("Bulk add error", e);
        }
      }

      // Update Last Entry Timestamp (using the last processed date)
      try {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', currentProfile.id), {
          lastTimeEntry: serverTimestamp(),
          lastEntryIP: 'bulk'
        });
      } catch (e) { }

      setIsSubmitting(false);
      showToast(`${createdCount} ${t('entriesCreated') || 'Einträge erstellt'}.`, 'success');
      // Reset is handled below but let's force return here to avoid executing the single-entry logic
      return;
    }

    // ORIGINAL LOGIC (WORK) -> Single Day
    let finalStart = startTime;
    let finalEnd = endTime;
    let finalBreaks = [...breaks];

    // Logic for Leave: Auto-set times and clear breaks
    let calculatedDuration = 0;
    if (isLeave) {
      calculatedDuration = calculateAbsenceHours({
        employee: currentProfile,
        date: entryDate,
        absenceType: entryType,
        shifts: shifts || [],
        leaveDuration: leaveDuration
      });
      finalStart = '08:00';
      finalEnd = '16:00';
      finalBreaks = [];
    }

    // Check for overlapping entries
    const newStart = timeToDecimal(finalStart);
    const newEnd = timeToDecimal(finalEnd);

    const overlappingEntry = timeEntries.find(entry => {
      if (entry.userId !== currentProfile.id || entry.date !== entryDate) return false;

      const existingStart = timeToDecimal(entry.startTime);
      const existingEnd = timeToDecimal(entry.endTime);

      // Check if times overlap
      return (newStart < existingEnd && newEnd > existingStart);
    });

    if (overlappingEntry) {
      showToast(t('overlapError'), 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      const startDec = timeToDecimal(finalStart);
      const endDec = timeToDecimal(finalEnd);
      const grossDuration = endDec - startDec;

      // Calculate Break Duration from Array or Legacy State
      let breakDur = 0;

      // Migration: If no breaks array but legacy inputs filled, add them
      // ONLY if not leave (as leave has no breaks)
      if (!isLeave && finalBreaks.length === 0 && breakStartTime && breakEndTime) {
        finalBreaks.push({ start: breakStartTime, end: breakEndTime });
      }

      if (finalBreaks.length > 0) {
        for (const b of finalBreaks) {
          const bs = timeToDecimal(b.start);
          const be = timeToDecimal(b.end);
          if (be <= bs) {
            showToast(t('negativeDuration') || 'Fehler: Pause Ende muss nach Pause Start sein.', 'error');
            setIsSubmitting(false);
            return;
          }
          breakDur += (be - bs) * 60;
        }
      }

      if (!isLeave) {
        if (grossDuration > 6 && breakDur < 30) {
          if (!window.confirm(t('breakWarning6h') || 'Achtung: Bei über 6 Stunden Arbeit sind gesetzlich 30 Minuten Pause vorgeschrieben. Trotzdem speichern?')) {
            setIsSubmitting(false);
            return;
          }
        }
        if (grossDuration > 9 && breakDur < 45) {
          if (!window.confirm(t('breakWarning9h') || 'Achtung: Bei über 9 Stunden Arbeit sind gesetzlich 45 Minuten Pause vorgeschrieben. Trotzdem speichern?')) {
            setIsSubmitting(false);
            return;
          }
        }
      }

      let netDuration = grossDuration - (breakDur / 60);

      // Override duration if it's a leave
      if (isLeave) {
        netDuration = calculatedDuration;
      }

      // Allow 0 duration for leave (e.g., sick on an unscheduled day)
      if (netDuration < 0 || (!isLeave && netDuration === 0)) {
        showToast(t('negativeDuration') || 'Fehler: Arbeitszeit minus Pause ist negativ oder Null.', 'error');
        setIsSubmitting(false);
        return;
      }

      // Get IP
      let clientIP = 'unknown';
      try {
        const res = await fetch('https://api.ipify.org?format=json');
        const data = await res.json();
        clientIP = data.ip;
      } catch (e) {
        console.error('IP fetch failed', e);
      }

      const entryData = {
        userId: currentProfile.id,
        userName: currentProfile.name,
        date: entryDate,
        startTime: finalStart,
        endTime: finalEnd,
        breaks: finalBreaks,
        breakStartTime: finalBreaks.length > 0 ? finalBreaks[0].start : null,
        breakEndTime: finalBreaks.length > 0 ? finalBreaks[0].end : null,
        breakDuration: Number(breakDur),
        duration: netDuration,
        type: entryType || 'WORK', // Save Type
        monthKey: entryDate.substring(0, 7),
        ip: clientIP,
      };

      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'time_entries'), {
        ...entryData,
        createdAt: serverTimestamp()
      });

      // Update lastTimeEntry timestamp for the employee
      try {
        await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', currentProfile.id), {
          lastTimeEntry: serverTimestamp(),
          lastEntryIP: clientIP
        });
      } catch (e) {
        console.error('Error updating lastTimeEntry:', e);
      }

      // Reset Form
      setBreaks([]);
      setBreakStartTime('');
      setBreakEndTime('');

      showToast(t('saved'), 'success');
    } catch (e) { showToast(t('saveError'), 'error'); }
    setIsSubmitting(false);
  };

  const handleEntryDateChange = (e) => {
    setEntryDate(e.target.value);
    // Auto-update end date if it's currently earlier than new start
    if (endDate < e.target.value) {
      setEndDate(e.target.value);
    }
  };

  // Shift Management Functions
  const checkShiftConflict = (employeeId, date, startTime, endTime, excludeShiftId = null) => {
    const newStart = timeToDecimal(startTime);
    const newEnd = timeToDecimal(endTime);

    return shifts.find(shift => {
      if (shift.id === excludeShiftId) return false;
      if (shift.employeeId !== employeeId || shift.date !== date) return false;

      const existingStart = timeToDecimal(shift.startTime);
      const existingEnd = timeToDecimal(shift.endTime);

      return (newStart < existingEnd && newEnd > existingStart);
    });
  };

  const handleCreateShift = async (employeeId, date, startTime, endTime, shiftType = '', type = 'WORK', notes = '') => {
    const employee = employees.find(e => e.id === employeeId);
    if (!employee) return showToast(t('userNotFound'), 'error');

    const conflict = checkShiftConflict(employeeId, date, startTime, endTime);
    if (conflict) return showToast(t('shiftConflict'), 'error');

    try {
      await addDoc(collection(db, 'artifacts', appId, 'public', 'data', 'shifts'), {
        employeeId,
        employeeName: employee.name,
        date,
        startTime,
        endTime,
        shiftType,
        type: type || 'WORK',
        notes,
        createdAt: serverTimestamp(),
        createdBy: currentProfile.id
      });
      showToast(t('saved'), 'success');
      setShowShiftModal(false);
      setEditingShift(null);
    } catch (e) {
      console.error(e);
      showToast(t('saveError'), 'error');
    }
  };

  const handleUpdateShift = async (shiftObj, updates) => {
    const shiftId = typeof shiftObj === 'object' ? shiftObj.id : shiftObj;
    const shiftType = typeof shiftObj === 'object' ? shiftObj.type : 'WORK';
    const shiftCollection = typeof shiftObj === 'object' ? shiftObj._collection : null;

    const isTimeEntry = shiftCollection ? shiftCollection === 'time_entries' : (shiftType === 'SICK' || shiftType === 'VACATION' || shiftType === 'TIME_TRACKING');
    const shift = isTimeEntry ? timeEntries.find(s => s.id === shiftId) : shifts.find(s => s.id === shiftId);

    if (!shift) return;

    if (updates.startTime || updates.endTime || updates.date) {
      const newStartTime = updates.startTime || shift.startTime;
      const newEndTime = updates.endTime || shift.endTime;
      const newDate = updates.date || shift.date;

      if (!isTimeEntry) {
        const conflict = checkShiftConflict(shift.employeeId || shift.userId, newDate, newStartTime, newEndTime, shiftId);
        if (conflict) return showToast(t('shiftConflict'), 'error');
      }
    }

    try {
      const collectionName = shiftCollection || (isTimeEntry ? 'time_entries' : 'shifts');

      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', collectionName, shiftId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      showToast(t('saved'), 'success');
      setShowShiftModal(false);
      setEditingShift(null);
    } catch (e) {
      console.error(e);
      showToast(t('saveError'), 'error');
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleDeleteShift = async (shift) => {
    const shiftId = typeof shift === 'object' ? shift.id : shift;
    const shiftType = typeof shift === 'object' ? shift.type : 'WORK';
    const shiftCollection = typeof shift === 'object' ? shift._collection : null;

    if (!window.confirm(t('confirmDelete'))) return;

    try {
      if (shiftCollection) {
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', shiftCollection, shiftId));
      } else if (shiftType === 'SICK' || shiftType === 'VACATION' || shiftType === 'TIME_TRACKING') {
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'time_entries', shiftId));
      } else {
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'shifts', shiftId));
      }
      showToast(t('deleted'), 'success');
    } catch (e) {
      console.error(e);
      showToast(t('saveError'), 'error');
    }
  };

  const getShiftsForWeek = (weekStart) => {
    const start = new Date(weekStart);
    start.setHours(0, 0, 0, 0);
    const end = new Date(start);
    end.setDate(start.getDate() + 7);

    return shifts.filter(shift => {
      const shiftDate = new Date(shift.date);
      return shiftDate >= start && shiftDate < end;
    });
  };

  // eslint-disable-next-line no-unused-vars
  const getShiftsForEmployee = (employeeId, weekStart) => {
    const weekShifts = getShiftsForWeek(weekStart);
    return weekShifts.filter(shift => shift.employeeId === employeeId);
  };

  const handleShiftModalSave = (formData) => {
    if (editingShift) {
      // Updating existing shift
      handleUpdateShift(editingShift, {
        date: formData.date,
        startTime: formData.startTime,
        endTime: formData.endTime,
        shiftType: formData.shiftType,
        type: formData.type || 'WORK',
        notes: formData.notes
      });
    } else {
      // Creating new shift
      handleCreateShift(
        formData.employeeId,
        formData.date,
        formData.startTime,
        formData.endTime,
        formData.shiftType,
        formData.type || 'WORK',
        formData.notes
      );
    }
  };

  // Splash Screen Timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSplash(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Auth Listener: Sitzung kommt jetzt von Firebase Auth (Custom Token)
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (fbUser) => {
      if (fbUser && fbUser.isAnonymous) {
        // Alte anonyme Sitzung aus der Vorgängerversion verwerfen
        signOut(auth).catch(console.error);
        setUser(null);
        setAuthReady(true);
        return;
      }
      setUser(fbUser ? { uid: fbUser.uid } : null);
      setAuthReady(true);
    });
    try { localStorage.removeItem('currentProfileId'); } catch (e) { /* Altlast der Vorgängerversion */ }
    return unsub;
  }, []);

  // Eigenes Profil live laden; deaktivierte/gesperrte Konten werden abgemeldet
  useEffect(() => {
    if (!user) { setMyProfile(null); return; }
    const endSession = (msg) => {
      setAuthError(msg);
      signOut(auth).catch(console.error);
    };
    return onSnapshot(
      doc(db, 'artifacts', appId, 'public', 'data', 'employees', user.uid),
      (snap) => {
        if (!snap.exists()) return endSession(t('userNotFound'));
        const profile = { id: snap.id, ...snap.data() };
        if (profile.approved === false) return endSession(t('accountPending') || 'Account pending approval.');
        if (isEmployeeDisabled(profile)) return endSession(t('accountDisabled') || 'Dieser Account wurde deaktiviert.');
        setMyProfile(profile);
      },
      (err) => {
        console.error('Profil-Listener:', err);
        endSession('Sitzung abgelaufen. Bitte erneut anmelden.');
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const staffEmployees = employees.filter(e => e.role !== 'display');
  const profileLoaded = !!myProfile;
  const isAdminRole = myProfile?.role === 'admin';
  const isDisplayRole = myProfile?.role === 'display';

  // Daten-Listener: Admins sehen alles, Mitarbeiter nur ihre eigenen Daten
  useEffect(() => {
    if (!user || !profileLoaded) return;
    const col = (name) => collection(db, 'artifacts', appId, 'public', 'data', name);
    const scoped = (name, field) => (isAdminRole ? col(name) : query(col(name), where(field, '==', user.uid)));
    const toList = (snapshot) => snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    const onErr = (name) => (err) => console.error(`Listener ${name}:`, err);

    const unsubs = [];
    if (isDisplayRole) {
      // Anzeige-Gerät: nur der komplette Schichtplan, live inkl. Offline-Status
      unsubs.push(onSnapshot(col('shifts'), { includeMetadataChanges: true }, (snap) => {
        setShifts(toList(snap));
        const offline = snap.metadata.fromCache;
        setBoardStatus(prev => ({ offline, lastSync: offline ? prev.lastSync : new Date() }));
      }, (err) => { onErr('shifts')(err); setBoardStatus(prev => ({ ...prev, offline: true })); }));
      return () => { unsubs.forEach(u => u()); setShifts([]); };
    }
    if (isAdminRole) {
      unsubs.push(onSnapshot(col('employees'), (snap) => setEmployees(toList(snap)), onErr('employees')));
    }
    unsubs.push(onSnapshot(scoped('time_entries', 'userId'), (snap) => setTimeEntries(toList(snap)), onErr('time_entries')));
    unsubs.push(onSnapshot(scoped('shifts', 'employeeId'), (snap) => setShifts(toList(snap)), onErr('shifts')));
    unsubs.push(onSnapshot(scoped('monthly_closings', 'userId'), (snap) => setMonthlyClosings(toList(snap)), onErr('monthly_closings')));

    return () => {
      unsubs.forEach(u => u());
      setEmployees([]); setTimeEntries([]); setShifts([]); setMonthlyClosings([]);
    };
  }, [user, profileLoaded, isAdminRole, isDisplayRole]);

  // Mitarbeiter kennen nur ihr eigenes Profil; aktuelles Profil folgt dem Live-Profil
  useEffect(() => {
    if (!isAdminRole) setEmployees(myProfile ? [myProfile] : []);
    setCurrentProfile(myProfile);
  }, [myProfile, isAdminRole]);

  // Neue App-Version automatisch laden, wenn die App nach >10 Min. wieder in den Vordergrund kommt
  useEffect(() => {
    const MAIN_JS = /\/static\/js\/main\.[a-f0-9]+\.js/;
    const current = Array.from(document.scripts).map(sc => sc.src).find(src => MAIN_JS.test(src));
    if (!current) return; // Entwicklungsmodus
    let hiddenSince = null;
    const checkForUpdate = async () => {
      if (document.visibilityState === 'hidden') { hiddenSince = Date.now(); return; }
      const wasAwayLong = hiddenSince && Date.now() - hiddenSince > 10 * 60 * 1000;
      hiddenSince = null;
      if (!wasAwayLong) return;
      try {
        const html = await (await fetch('/index.html', { cache: 'no-store' })).text();
        const latest = html.match(MAIN_JS);
        if (latest && !current.endsWith(latest[0])) window.location.reload();
      } catch (e) { /* offline – später erneut */ }
    };
    document.addEventListener('visibilitychange', checkForUpdate);
    return () => document.removeEventListener('visibilitychange', checkForUpdate);
  }, []);

  // Handle print-view class for reliable native printing
  useEffect(() => {
    if (printReportData || bulkPrintData) {
      document.body.classList.add('print-view-active');
    } else {
      document.body.classList.remove('print-view-active');
    }
    return () => document.body.classList.remove('print-view-active');
  }, [printReportData, bulkPrintData]);

  const getEmployeeStats = (empId, monthOverride = null) => {
    const targetMonth = monthOverride || adminSelectedMonth || getCurrentMonthKey();
    const employee = employees.find(e => e.id === empId);
    const targetHours = getEffectiveMaxHours(employee, targetMonth);

    const balances = calculateMonthlyBalances(empId, timeEntries, employee, targetMonth, monthlyClosings, employee?.employmentType);
    return balances[targetMonth] || { worked: 0, carryOver: 0, total: 0, balance: -targetHours, paidOrDue: targetHours };
  };



  const handlePrintUserReport = (emp) => {
    const userEntries = timeEntries.filter(e => e.userId === emp.id && e.monthKey === adminSelectedMonth);
    if (!userEntries || userEntries.length === 0) return showToast(t('fillData'), 'error');

    // Calculate stats to get carryover and balances
    const stats = getEmployeeStats(emp.id, adminSelectedMonth);

    setPrintReportData({
      employee: emp,
      entries: userEntries,
      month: adminSelectedMonth,
      stats: stats // Pass stats to the report
    });
  };

  const handleEmployeePrint = () => {
    const userEntries = timeEntries.filter(e => e.userId === currentProfile.id && e.monthKey === employeeViewMonth);
    if (!userEntries || userEntries.length === 0) return showToast(t('fillData'), 'error');

    // Calculate stats to get carryover and balances
    const stats = getEmployeeStats(currentProfile.id, employeeViewMonth);

    setPrintReportData({
      employee: currentProfile,
      entries: sortEntries(userEntries),
      month: employeeViewMonth,
      stats: stats // Pass stats to the report
    });
  };





  const handleDeleteEmployee = async () => {
    if (!employeeToDelete) return;

    // Prevent self-deletion
    if (employeeToDelete.id === currentProfile.id) {
      showToast(t('cannotDeleteSelf'), 'error');
      setEmployeeToDelete(null);
      return;
    }

    try {
      // Delete all time entries for this employee
      const userEntries = timeEntries.filter(e => e.userId === employeeToDelete.id);
      for (const entry of userEntries) {
        await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'time_entries', entry.id));
      }

      // Delete the employee
      await deleteDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', employeeToDelete.id));

      setEmployeeToDelete(null);
      showToast(t('employeeDeleted'), 'success');
    } catch (error) {
      console.error('Error deleting employee:', error);
      showToast(t('saveError'), 'error');
    }
  };

  // ADMIN: Toggle Role
  // ADMIN: Konto als Anzeige-Gerät (Aushang) markieren bzw. zurück zu Mitarbeiter
  const handleSetDisplayRole = async (emp, makeDisplay) => {
    const msg = makeDisplay
      ? `${emp.name} als Anzeige-Gerät verwenden?\n\nDas Konto sieht danach nur noch den Wochen-Schichtplan und kann keine Zeiten mehr erfassen.`
      : `${emp.name} wieder als normales Mitarbeiterkonto verwenden?`;
    if (!window.confirm(msg)) return;
    try {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', emp.id), { role: makeDisplay ? 'display' : 'employee' });
      showToast(t('toastRoleChanged'), 'success');
    } catch (e) { console.error(e); showToast(t('saveError'), 'error'); }
  };

  const handleToggleAdmin = async (emp) => {
    const newRole = emp.role === 'admin' ? 'employee' : 'admin';
    if (window.confirm(newRole === 'admin' ? t('makeAdmin') + '?' : t('removeAdmin') + '?')) {
      await updateDoc(doc(db, 'artifacts', appId, 'public', 'data', 'employees', emp.id), { role: newRole });
      showToast(t('toastRoleChanged'), 'info');
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword.trim() || !currentProfile) return;
    // Validate PIN
    if (!isValidPin(newPassword.trim())) {
      alert(t('pinError') || 'PIN muss 6 Zahlen haben.');
      return;
    }

    try {
      await callFn('changePin', { newPin: newPassword.trim() });
      setShowPasswordChange(false); setNewPassword('');
      showToast(t('passChanged'), 'success');
    } catch (e) { showToast(fnErrorMessage(e), 'error'); }
  };

  // ADMIN: PIN eines Mitarbeiters neu setzen (PINs sind nicht mehr einsehbar)
  const handleAdminResetPin = async (emp) => {
    const input = window.prompt(`Neuer 6-stelliger PIN für ${emp.name}:\n(${emp.name} muss ihn beim nächsten Login selbst ändern.)`);
    if (input === null) return;
    const pin = input.trim();
    if (!isValidPin(pin)) return showToast(t('pinError') || 'PIN muss 6 Zahlen haben.', 'error');
    try {
      await callFn('adminResetPin', { employeeId: emp.id, newPin: pin });
      showToast(`PIN für ${emp.name} zurückgesetzt.`, 'success');
    } catch (e) {
      console.error(e);
      showToast(fnErrorMessage(e), 'error');
    }
  };



  const handleSendReport = (emp) => {
    if (!emp.email) return showToast(t('toastNoMail'), 'error');
    const currentMonthKey = getCurrentMonthKey();
    const monthlyEntries = timeEntries.filter(t => t.userId === emp.id && t.monthKey === currentMonthKey);
    const monthlyHours = monthlyEntries.reduce((s, e) => s + (Number(e.duration) || 0), 0);
    const subject = `${t('printTitle')} - ${emp.name} - ${currentMonthKey}`;
    const targetHours = getEffectiveMaxHours(emp, currentMonthKey);
    let body = `${t('reportBody')} ${emp.name},\n\n${t('reportBody2')}\n\n--------------------------------\n${t('monthlyStatus').toUpperCase()}\n--------------------------------\n${t('current')}: \t${(monthlyHours || 0).toFixed(2)} h\n${t('max')}:    \t${(targetHours || 0).toFixed(2)} h\n`;
    const pct = (monthlyHours / (targetHours || 1)) * 100;
    body += `${t('stand')}:   \t${Math.round(pct)}%\n\n--------------------------------\n${t('history').toUpperCase()} (${currentMonthKey})\n--------------------------------\n`;
    const sortedEntries = [...monthlyEntries].sort((a, b) => b.date.localeCompare(a.date));
    sortedEntries.forEach(e => { const dateStr = new Date(e.date).toLocaleDateString('de-DE'); body += `${dateStr} | ${e.startTime}-${e.endTime} | ${(Number(e.duration) || 0).toFixed(2)} h\n`; });
    body += `\n\nVG`;
    window.location.href = `mailto:${emp.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleBulkPrint = (entries) => {
    const uniqueEmployeeIds = [...new Set(entries.map(e => e.userId))];
    const data = uniqueEmployeeIds.map(empId => {
      const employee = employees.find(e => e.id === empId);
      const userEntries = entries.filter(e => e.userId === empId && e.monthKey === adminSelectedMonth);
      // Calculate stats for each employee
      const stats = getEmployeeStats(empId, adminSelectedMonth);
      return { employee, userEntries, stats };
    }).filter(item => item.employee && item.userEntries.length > 0);
    if (data.length === 0) return showToast(t('fillData'), 'error');
    setBulkPrintData(data);
  };

  const handleAdminEntriesPrint = () => {
    // If a specific employee is selected, print their individual report with stats
    if (adminSelectedEmployee) {
      const emp = employees.find(e => e.id === adminSelectedEmployee);
      if (emp) {
        handlePrintUserReport(emp);
        return;
      }
    }

    let filteredEntries = filterEntries(timeEntries, filterMode, adminSelectedMonth);
    if (!filteredEntries || filteredEntries.length === 0) return showToast(t('fillData'), 'error');
    handleBulkPrint(filteredEntries);
  };

  // --- VIEWS ---
  if (showSplash) {
    return <SplashScreen logoUrl={LOGO_URL} />;
  }

  if (bulkPrintData) {
    return (
      <BulkPrintView
        data={bulkPrintData}
        month={adminSelectedMonth}
        onDone={() => setBulkPrintData(null)}
        companyAddress={companyAddress}
        LOGO_URL={LOGO_URL}
        t={t}
        formatHours={formatHours}
      />
    );
  }

  if (!authReady || (user && !currentProfile)) return <div className="flex h-screen items-center justify-center text-gray-500 dark:bg-black dark:text-slate-300">Loading...</div>;

  // Pflicht-PIN-Wechsel (nach Migration oder Admin-Reset)
  if (pendingLogin || (currentProfile && currentProfile.mustChangePin)) {
    return (
      <MainWrapper isRTL={isRTL} onOpenImpressum={() => setShowImpressum(true)} onOpenDatenschutz={() => setShowDatenschutz(true)}>
        <Toast notification={notification} onClose={() => setNotification(null)} />
        {showSplash && <SplashScreen logoUrl={LOGO_URL} />}
        {showImpressum && <ImpressumModal onClose={() => setShowImpressum(false)} />}
        {showDatenschutz && <DatenschutzModal onClose={() => setShowDatenschutz(false)} />}
        <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 py-8 bg-gray-100 dark:bg-black">
          <div className="max-w-md w-full bg-white dark:bg-zinc-900 rounded-xl shadow-lg p-8 relative">
            <div className="text-center mb-6">
              <Shield className="h-12 w-12 mx-auto text-accent-mint mb-4" />
              <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">{t('securityUpdateTitle')}</h2>
              <p className="text-sm text-gray-600 dark:text-slate-300">{t('securityUpdateMsg')}</p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-slate-300">{t('newPasswordTitle')}</label>
                <input
                  dir="ltr"
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={upgradePassword}
                  onChange={e => setUpgradePassword(e.target.value)}
                  className="w-full p-3 border dark:border-zinc-700 rounded-lg text-center text-2xl tracking-widest bg-white dark:bg-zinc-800 dark:text-white"
                  placeholder="000000"
                />
              </div>
              <Button onClick={handleUpgradePassword} disabled={authBusy} className="w-full py-3 rounded-xl font-bold text-black bg-accent-mint hover:bg-emerald-400 shadow-md mt-4">
                {t('saveAndLogin')}
              </Button>
              <div className="text-center"><Button variant="link" onClick={handleLogout} className="text-sm text-gray-500 dark:text-slate-400 hover:underline">{t('logout')}</Button></div>
            </div>
          </div>
        </div>
      </MainWrapper>
    );
  }

  // Login View
  if (!currentProfile) {
    return (
      <MainWrapper isRTL={isRTL}>
        <Toast notification={notification} onClose={() => setNotification(null)} />
        <div className="flex-grow flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white dark:bg-zinc-900 rounded-xl shadow-lg p-8 relative transition-colors duration-300">
            <div className="absolute top-4 right-4 flex space-x-2"><DarkModeToggle darkMode={darkMode} toggleDarkMode={toggleDarkMode} /><LanguageSwitcher lang={lang} setLang={setLang} /></div>
            <div className="text-center mb-6 mt-4"><img src={LOGO_URL} alt="Logo" className="h-16 mx-auto mb-6 object-contain" /><h1 className="text-2xl font-bold text-gray-800 dark:text-white">{authMode === 'login' ? t('loginTitle') : t('registerTitle')}</h1></div>
            <div className="space-y-4">
              {authError && <div className="bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 p-3 rounded text-sm text-center">{authError}</div>}
              {authMode === 'login' ? (
                <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300">{t('nameLabel')}</label><input dir="ltr" type="text" value={loginName} onChange={e => setLoginName(e.target.value)} className="w-full p-3 border dark:border-zinc-700 rounded-lg text-left bg-white dark:bg-zinc-800 dark:text-white" placeholder="Benutzername" /></div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300">{t('firstNameLabel')} *</label><input dir="ltr" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full p-3 border dark:border-zinc-700 rounded-lg text-left bg-white dark:bg-zinc-800 dark:text-white" placeholder="Max" required /></div>
                    <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300">{t('lastNameLabel')} *</label><input dir="ltr" type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full p-3 border dark:border-zinc-700 rounded-lg text-left bg-white dark:bg-zinc-800 dark:text-white" placeholder="Mustermann" required /></div>
                  </div>
                  <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300">{t('usernameLabel')} *</label><input dir="ltr" type="text" value={username} onChange={e => setUsername(e.target.value)} className="w-full p-3 border dark:border-zinc-700 rounded-lg text-left bg-white dark:bg-zinc-800 dark:text-white" placeholder="max.mustermann" required /></div>
                </div>
              )}
              <div><label className="text-sm font-medium text-gray-700 dark:text-slate-300">{t('passwordLabel')}</label><input dir="ltr" type="password" inputMode="numeric" pattern="[0-9]*" maxLength={6} value={loginPassword} onChange={e => setLoginPassword(e.target.value)} className="w-full p-3 border dark:border-zinc-700 rounded-lg text-left bg-white dark:bg-zinc-800 dark:text-white" placeholder="******" /></div>
              {authMode === 'register' && (<><div><label className="text-sm font-medium text-gray-700 dark:text-slate-300">{t('emailLabel')}</label><input dir="ltr" type="email" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} className="w-full p-3 border dark:border-zinc-700 rounded-lg text-left bg-white dark:bg-zinc-800 dark:text-white" placeholder="max@firma.de" /></div></>)}
              <Button onClick={authMode === 'login' ? handleLogin : handleRegister} disabled={authBusy} className="w-full py-3 rounded-xl font-bold text-black bg-accent-mint hover:bg-emerald-400 shadow-md mt-4">{authMode === 'login' ? t('loginButton') : t('registerButton')}</Button>
              <div className="text-center"><Button variant="link" onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setAuthError(''); }} className="text-sm text-accent-mint dark:text-accent-mint hover:underline">{authMode === 'login' ? t('toggleRegister') : t('toggleLogin')}</Button></div>
            </div>
          </div>
        </div>
      </MainWrapper>
    );
  }

  // Aushang (Tablet im Laden)
  if (currentProfile.role === 'display') {
    return <ShiftBoard shifts={shifts} logoUrl={LOGO_URL} onLogout={handleLogout} lastSync={boardStatus.lastSync} offline={boardStatus.offline} />;
  }

  // --- VIEWS ---
  // Print View
  if (printReportData) {
    const { employee: emp, entries, month, stats } = printReportData;

    const handleDownloadPdf = async () => {
      try {
        const element = printRef.current;
        const pages = element.querySelectorAll('.pdf-page');
        const pdf = new jsPDF('p', 'mm', 'a4');
        // Linker Bundsteg (~20mm) fürs Lochen/Abheften, rechts/oben schmaler
        const marginTop = 12;
        const marginLeft = 20;
        const marginRight = 12;
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const contentWidth = pdfWidth - marginLeft - marginRight;

        const fileName = `Stundenzettel_${emp.name}_${month}.pdf`;

        const renderPage = async (pageEl, isFirst) => {
          const canvas = await html2canvas(pageEl, { scale: 2, useCORS: true, allowTaint: true });
          const data = canvas.toDataURL('image/png');
          const pdfHeight = (canvas.height * contentWidth) / canvas.width;
          if (!isFirst) pdf.addPage();
          pdf.addImage(data, 'PNG', marginLeft, marginTop, contentWidth, pdfHeight);
        };

        if (pages.length > 0) {
          for (let i = 0; i < pages.length; i++) {
            await renderPage(pages[i], i === 0);
          }
        } else {
          await renderPage(element, true);
        }

        // iOS PWA Fix: use navigator.share if available
        const blob = pdf.output('blob');
        const file = new File([blob], fileName, { type: 'application/pdf' });
        
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({
              files: [file],
              title: fileName,
            });
          } catch (err) {
            console.error("Share failed", err);
            // Fallback
            pdf.save(fileName);
          }
        } else {
          pdf.save(fileName);
        }
      } catch (error) {
        console.error("PDF Generation Error: ", error);
        alert("Fehler beim Erstellen der PDF");
      }
    };


    return (
      <MainWrapper isRTL={isRTL}>
        <div className="fixed inset-0 bg-gray-100 z-[100] flex flex-col print:relative print:block print:bg-white text-black">
          {/* TOOLBAR */}
          <div 
            className="flex justify-end items-center px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top,2rem))] print:hidden gap-2 shrink-0 bg-white dark:bg-zinc-900 border-b dark:border-zinc-800 shadow-sm w-full top-0 sticky z-50 overflow-x-auto"
          >
            <Button onClick={handleDownloadPdf} className="flex items-center px-4 py-2 bg-red-50 text-red-700 rounded-lg font-medium hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400 dark:hover:bg-red-900/30 whitespace-nowrap h-auto"><Download className="w-4 h-4 mr-2" /> PDF</Button>
            <Button onClick={() => window.print()} className="flex items-center px-4 py-2 bg-gray-100 rounded-lg text-gray-700 font-medium hover:bg-gray-200 dark:bg-zinc-800 dark:text-slate-300 dark:hover:bg-zinc-700 whitespace-nowrap h-auto"><Printer className="w-4 h-4 mr-2" /> {t('print')}</Button>
            <Button onClick={() => setPrintReportData(null)} className="flex items-center px-4 py-2 bg-gray-100 rounded-lg text-gray-700 font-medium hover:bg-gray-200 dark:bg-zinc-800 dark:text-slate-300 dark:hover:bg-zinc-700 whitespace-nowrap h-auto"><X className="w-4 h-4 mr-2" /> {t('close')}</Button>
          </div>

          {/* SCROLLABLE CONTENT AREA */}
          <div className="flex-1 overflow-auto p-4 sm:p-8 print:p-0 print:overflow-visible">
            <div id="print-content" className="max-w-4xl mx-auto min-w-[794px] print:min-w-0 bg-white dark:bg-zinc-900 sm:shadow-lg sm:p-8 p-4 print:shadow-none print:max-w-none print:w-full print:p-0" ref={printRef}>
              <PrintableReport
                employee={emp}
                entries={entries}
                month={month}
                companyAddress={companyAddress}
                LOGO_URL={LOGO_URL}
                t={t}
                formatHours={formatHours}
                stats={stats}
              />
            </div>
          </div>
        </div>
      </MainWrapper>
    );
  }

  // Admin View
  if (currentProfile.role === 'admin') {
    let filteredEntries = filterEntries(timeEntries, filterMode, adminSelectedMonth);
    if (adminSelectedEmployee) {
      filteredEntries = filteredEntries.filter(e => e.userId === adminSelectedEmployee);
    }
    let grandTotal = 0; filteredEntries.forEach(e => grandTotal += (Number(e.duration) || 0));
    const totalsMap = {}; filteredEntries.forEach(e => { if (!totalsMap[e.userName]) totalsMap[e.userName] = 0; totalsMap[e.userName] += (Number(e.duration) || 0); });

    return (
      <MainWrapper isRTL={isRTL} onOpenImpressum={() => setShowImpressum(true)} onOpenDatenschutz={() => setShowDatenschutz(true)}>
        <Toast notification={notification} onClose={() => setNotification(null)} />
        {showImpressum && <ImpressumModal onClose={() => setShowImpressum(false)} />}
        {showDatenschutz && <DatenschutzModal onClose={() => setShowDatenschutz(false)} />}
        <div className="pb-20 print:bg-white">
          <AppHeader title={t('adminConsole')} subtitle={`${t('loggedInAs')} ${currentProfile.name} `} icon={Shield} colorClass="purple" isRTL={isRTL} lang={lang} setLang={setLang} showPasswordChange={showPasswordChange} setShowPasswordChange={setShowPasswordChange} newPassword={newPassword} setNewPassword={setNewPassword} handleChangePassword={handleChangePassword} onLogout={handleLogout} t={t} darkMode={darkMode} toggleDarkMode={toggleDarkMode}>
            <div className="mt-2 overflow-x-auto no-scrollbar">
              <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-gray-100 dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800">
                {[
                  { id: 'dashboard', label: 'Dashboard', Icon: LayoutDashboard },
                  { id: 'employees', label: t('employeeOverview'), Icon: Users },
                  { id: 'entries', label: t('entriesAll'), Icon: List },
                  { id: 'schedule', label: t('scheduleTab'), Icon: CalendarDays },
                ].map(({ id, label, Icon }) => {
                  const on = activeAdminTab === id;
                  return (
                    <button
                      key={id}
                      onClick={() => setActiveAdminTab(id)}
                      className={`flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${on
                        ? 'bg-accent-mint text-black shadow-sm'
                        : 'text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          </AppHeader>
          <EditModal editingEntry={editingEntry} setEditingEntry={setEditingEntry} handleUpdateEntry={handleUpdateEntry} t={t} isRTL={isRTL} employees={staffEmployees} />
          <DeleteEmployeeModal employeeToDelete={employeeToDelete} setEmployeeToDelete={setEmployeeToDelete} handleDeleteEmployee={handleDeleteEmployee} t={t} isRTL={isRTL} />
          <DeactivateEmployeeModal
            deactivatingEmployee={deactivatingEmployee}
            setDeactivatingEmployee={setDeactivatingEmployee}
            deactivateDate={deactivateDate}
            setDeactivateDate={setDeactivateDate}
            handleDeactivateEmployee={handleDeactivateEmployee}
            t={t}
            isRTL={isRTL}
          />
          <ShiftModal
            show={showShiftModal}
            onClose={() => { setShowShiftModal(false); setEditingShift(null); }}
            editingShift={editingShift}
            employees={staffEmployees}
            onSave={handleShiftModalSave}
            t={t}
            isRTL={isRTL}
          />

          <SettlementModal
            show={showSettlementModal}
            onClose={() => setShowSettlementModal(false)}
            employee={activeSettlementEmployee}
            monthlyClosings={monthlyClosings}
            timeEntries={timeEntries}
            onSave={handleSaveSettlement}
            t={t}
            isRTL={isRTL}
            formatHours={formatHours}
          />
          <TargetHoursModal
            show={showTargetHoursModal}
            onClose={() => setShowTargetHoursModal(false)}
            employee={activeTargetHoursEmployee}
            onSave={handleUpdateTargetHoursHistory}
            t={t}
            isRTL={isRTL}
            formatHours={formatHours}
          />
          <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 print:p-0 print:max-w-none">

            {/* Overview Tab Content */}
            {/* Dashboard Tab Content */}
            {activeAdminTab === 'dashboard' && (
              <>
                <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                    <LayoutDashboard className="w-5 h-5 mr-2 text-accent-mint" />
                    Dashboard
                  </h3>
                  <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleExportData}
                      className="flex items-center text-sm font-medium transition-colors"
                      title="Download Backup (JSON)"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Backup
                    </Button>
                    <div className="text-sm text-gray-500 dark:text-slate-400">
                      {new Date().toLocaleDateString(lang, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </div>
                </div>

                <CoverageView
                  entries={timeEntries}
                  employees={staffEmployees}
                  t={t}
                  isRTL={isRTL}
                  externalMonth={adminSelectedMonth}
                  onMonthChange={setAdminSelectedMonth}
                  lang={lang}
                />



              </>
            )}

            {/* Employees Tab Content */}
            {activeAdminTab === 'employees' && (
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                  <h3 className="text-xl font-bold text-purple-700 dark:text-purple-400 flex items-center">
                    <Users className="w-5 h-5 mr-2" />
                    {t('employeeOverview')}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                    <Button variant="ghost" size="sm" onClick={() => handleBulkPrint(timeEntries)} className="flex-1 sm:flex-none flex items-center justify-center bg-emerald-50 text-emerald-600 dark:text-accent-mint hover:bg-emerald-100 dark:hover:bg-accent-mint/20 transition-colors whitespace-nowrap">
                      <Printer className="w-4 h-4 mr-2" /> {t('printAll') || 'Alle Drucken'}
                    </Button>
                    <label className="text-sm text-gray-600 dark:text-slate-400 font-medium hidden sm:block">
                      {t('selectMonth') || 'Monat'}:
                    </label>
                    <input
                      type="month"
                      value={adminSelectedMonth}
                      onChange={(e) => setAdminSelectedMonth(e.target.value)}
                      className="text-sm p-1.5 border dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-700 dark:text-white outline-none focus:ring-2 focus:ring-accent-lilac shadow-sm transition-all"
                    />
                  </div>
                </div>

                {/* ADMIN OVERVIEW */}
                {employees.filter(e => e.role === 'admin').length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-zinc-500 mb-2">Admins</p>
                    <div className="space-y-2">
                      {employees.filter(e => e.role === 'admin').map(emp => (
                        <div key={emp.id} className="rounded-xl bg-white dark:bg-zinc-900 border border-purple-200 dark:border-zinc-800 px-4 py-3">
                          {editingNameId === emp.id ? (
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full">
                              <input type="text" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-full sm:w-24" placeholder={t('firstNameLabel')} autoFocus />
                              <input type="text" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-full sm:w-24" placeholder={t('lastNameLabel')} />
                              <input type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-full sm:w-24" placeholder={t('usernameLabel')} />
                              <div className="flex gap-2">
                                <Button variant="ghost" size="iconSm" onClick={() => handleUpdateName(emp.id)} className="text-green-600 hover:text-green-800"><Save className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="iconSm" onClick={() => setEditingNameId(null)} className="text-red-600 hover:text-red-800"><X className="w-4 h-4" /></Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <span className="w-2.5 h-2.5 rounded-full bg-accent-lilac flex-shrink-0"></span>
                                <div className="min-w-0">
                                  <div className="font-semibold dark:text-white flex items-center gap-1.5 flex-wrap">
                                    <span>{emp.firstName || emp.name.split(' ')[0]}</span>
                                    <span className="font-normal text-gray-500 dark:text-slate-400">{emp.lastName || emp.name.split(' ').slice(1).join(' ')}</span>
                                    <Button variant="ghost" size="iconSm" onClick={() => {
                                      setEditingNameId(emp.id);
                                      setNewFirstName(emp.firstName || emp.name.split(' ')[0]);
                                      setNewLastName(emp.lastName || emp.name.split(' ').slice(1).join(' '));
                                      setNewUsername(emp.username || '');
                                    }} className="text-gray-400 hover:text-gray-600"><Edit2 className="w-3 h-3" /></Button>
                                  </div>
                                  <div className="text-xs text-gray-400 truncate">@{emp.username || '-'}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent-lilac/15 text-accent-lilac border border-accent-lilac/30">{t('adminBadge')}</span>
                                <Button variant="ghost" size="icon" onClick={() => handleToggleAdmin(emp)} className="text-purple-500 hover:bg-purple-500/10" title={t('removeAdmin')}><UserCog className="w-4 h-4" /></Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ANZEIGE-GERÄTE (Aushang) */}
                {employees.filter(e => e.role === 'display').length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-zinc-500 mb-2">Anzeige-Geräte</p>
                    <div className="space-y-2">
                      {employees.filter(e => e.role === 'display').map(emp => (
                        <div key={emp.id} className="rounded-xl bg-white dark:bg-zinc-900 border border-sky-200 dark:border-zinc-800 px-4 py-3 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <CalendarDays className="w-4 h-4 text-sky-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="font-semibold dark:text-white truncate">{emp.name}</div>
                              <div className="text-xs text-gray-400 truncate">@{emp.username || '-'} · zeigt den Wochen-Schichtplan · {t('lastLogin')}: {formatTimestamp(emp.lastLogin, lang)}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 text-xs">
                            <button type="button" onClick={() => handleAdminResetPin(emp)} className="underline text-gray-500 dark:text-slate-400 hover:text-accent-mint">PIN zurücksetzen</button>
                            <button type="button" onClick={() => handleSetDisplayRole(emp, false)} className="underline text-gray-500 dark:text-slate-400 hover:text-accent-mint">wieder Mitarbeiter</button>
                            <Button variant="ghost" size="icon" onClick={() => setEmployeeToDelete(emp)} className="text-red-400 hover:bg-red-500/10" title={t('delete')}><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PENDING APPROVALS */}
                {employees.filter(e => e.approved === false).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-500/80 mb-2 flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" />
                      {t('pendingApprovals')}
                    </p>
                    <div className="space-y-2">
                      {employees.filter(e => e.approved === false).map(emp => (
                        <div key={emp.id} className="rounded-xl bg-amber-50 dark:bg-amber-500/5 border border-amber-200 dark:border-amber-500/30 px-4 py-3 flex justify-between items-center gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold dark:text-white truncate">
                              {emp.firstName || emp.name.split(' ')[0]} {emp.lastName || emp.name.split(' ').slice(1).join(' ')}
                            </div>
                            <div className="text-xs text-gray-400 truncate">@{emp.username || '-'}</div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button variant="ghost" size="icon" onClick={() => handleApproveUser(emp)} className="bg-emerald-500/15 text-accent-mint hover:bg-emerald-500/25" title={t('approve')}>
                              <CheckCircle className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleRejectUser(emp)} className="bg-red-500/15 text-red-400 hover:bg-red-500/25" title={t('reject')}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-zinc-500 pt-2">{t('employeeOverview')}</p>
                {employees.filter(e => e.role !== 'admin' && e.role !== 'display' && e.approved !== false && !isEmployeeDisabled(e)).map(emp => {
                  const stats = getEmployeeStats(emp.id);
                  const percentage = (stats.total / (stats.paidOrDue || 1)) * 100;
                  const over = (stats.worked / (stats.paidOrDue || 1)) * 100 > 100;
                  const expanded = expandedEmpIds.has(emp.id);
                  const isEditing = editingNameId === emp.id;
                  const empTypeLabel = { FULL_TIME: 'Festangestellt', MINI_JOB: 'Mini-Job', MIDI_JOB: 'Midi-Job' }[emp.employmentType || 'FULL_TIME'];
                  return (
                    <div key={emp.id} className="rounded-xl bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 overflow-hidden">
                      {/* Collapsed header — click to expand */}
                      <div onClick={() => toggleEmpExpanded(emp.id)} className="px-4 py-3 cursor-pointer select-none">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: percentage > 80 ? '#ef4444' : '#22c55e' }}></span>
                            <div className="min-w-0">
                              <div className="font-semibold dark:text-white truncate">
                                {emp.firstName || emp.name.split(' ')[0]} <span className="font-normal text-gray-500 dark:text-slate-400">{emp.lastName || emp.name.split(' ').slice(1).join(' ')}</span>
                              </div>
                              <div className="text-xs text-gray-400 truncate">@{emp.username || '-'} · {empTypeLabel}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div className="text-right">
                              <div className="text-sm font-semibold whitespace-nowrap">
                                <span className={over ? 'text-red-500' : 'text-accent-mint'}>{formatHours(stats.total)}</span>
                                <span className="text-gray-400 dark:text-slate-500"> / {formatHours(stats.paidOrDue)}</span>
                              </div>
                              <div className="text-[10px] text-gray-400">Ist / Soll</div>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        <div className="mt-2.5">
                          <ProgressBar current={stats.total} max={stats.paidOrDue} />
                        </div>
                      </div>

                      {/* Expanded body — details & settings */}
                      {expanded && (
                        <div className="px-4 pb-4 pt-3 border-t border-gray-200 dark:border-zinc-800 space-y-3">
                          {/* Name + edit */}
                          {isEditing ? (
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full">
                              <input type="text" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-full sm:w-24" placeholder={t('firstNameLabel')} autoFocus />
                              <input type="text" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-full sm:w-24" placeholder={t('lastNameLabel')} />
                              <input type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-full sm:w-24" placeholder={t('usernameLabel')} />
                              <div className="flex gap-2">
                                <Button variant="ghost" size="iconSm" onClick={() => handleUpdateName(emp.id)} className="text-green-600 hover:text-green-800"><Save className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="iconSm" onClick={() => setEditingNameId(null)} className="text-red-600 hover:text-red-800"><X className="w-4 h-4" /></Button>
                              </div>
                            </div>
                          ) : (
                            <button onClick={() => { setEditingNameId(emp.id); setNewFirstName(emp.firstName || emp.name.split(' ')[0]); setNewLastName(emp.lastName || emp.name.split(' ').slice(1).join(' ')); setNewUsername(emp.username || ''); }} className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-slate-300">
                              <Edit2 className="w-3 h-3" /> Name bearbeiten
                            </button>
                          )}

                          {/* Meta: login / entry / pin / ip */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                            <div className="flex items-center gap-1.5"><Clock className="w-3 h-3 flex-shrink-0" /><span>{t('lastLogin')}:</span><span className="font-mono text-gray-600 dark:text-slate-300">{formatTimestamp(emp.lastLogin, lang)}</span></div>
                            <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3 flex-shrink-0" /><span>{t('lastTimeEntry')}:</span><span className="font-mono text-gray-600 dark:text-slate-300">{formatTimestamp(emp.lastTimeEntry, lang)}</span></div>
                            <div className="flex items-center gap-1.5 flex-wrap col-span-1 sm:col-span-2"><Key className="w-3 h-3 flex-shrink-0" /><span>PIN:</span><button type="button" onClick={() => handleAdminResetPin(emp)} className="underline text-gray-600 dark:text-slate-300 hover:text-accent-mint">zurücksetzen</button>{emp.mustChangePin && <span className="text-[10px] text-amber-500">· Wechsel ausstehend</span>}{emp.lastEntryIP && <span className="text-[10px] text-gray-400">· IP <span className="font-mono">{emp.lastEntryIP}</span></span>}</div>
                          </div>

                          {/* Email + color */}
                          <div className="flex gap-2 items-center">
                            <input dir="ltr" type="email" className="flex-1 bg-gray-50 dark:bg-zinc-800/60 rounded-lg px-3 py-1.5 text-sm dark:text-slate-300 outline-none border border-transparent focus:border-accent-mint" placeholder="Email..." defaultValue={emp.email || ''} onBlur={(e) => handleUpdateEmail(emp.id, e.target.value)} />
                            <input type="color" value={emp.color || '#3b82f6'} onChange={(e) => handleUpdateColor(emp.id, e.target.value)} className="w-9 h-9 p-0 border-0 rounded-lg cursor-pointer flex-shrink-0 bg-transparent" title="Color" />
                          </div>

                          {/* Settings: max / weekly / employment type */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                            <label className="flex flex-col gap-1">
                              <span className="text-gray-500 dark:text-slate-500">Max-Std.</span>
                              <input dir="ltr" type="number" className="bg-gray-50 dark:bg-zinc-800/60 rounded-lg px-2.5 py-1.5 text-right dark:text-white outline-none border border-transparent focus:border-accent-mint" defaultValue={emp.maxHours} onBlur={(e) => handleUpdateMaxHours(emp.id, e.target.value)} />
                            </label>
                            <label className="flex flex-col gap-1">
                              <span className="text-gray-500 dark:text-slate-500">Wochenstd.</span>
                              <input dir="ltr" type="number" className="bg-gray-50 dark:bg-zinc-800/60 rounded-lg px-2.5 py-1.5 text-right dark:text-white outline-none border border-transparent focus:border-accent-mint" defaultValue={emp.weeklyHours || 0} onBlur={(e) => handleUpdateWeeklyHours(emp.id, e.target.value)} />
                            </label>
                            <label className="flex flex-col gap-1 col-span-2 sm:col-span-1">
                              <span className="text-gray-500 dark:text-slate-500">Beschäftigung</span>
                              <select value={emp.employmentType || 'FULL_TIME'} onChange={(e) => handleUpdateEmploymentType(emp.id, e.target.value)} className="bg-gray-50 dark:bg-zinc-800/60 rounded-lg px-2.5 py-1.5 dark:text-slate-300 outline-none border border-transparent focus:border-accent-mint cursor-pointer">
                                <option value="FULL_TIME">Festangestellt (Gleitzeit)</option>
                                <option value="MINI_JOB">Mini-Job (Stunden zählen)</option>
                                <option value="MIDI_JOB">Midi-Job (Stunden zählen)</option>
                              </select>
                            </label>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2 flex-wrap pt-1">
                            <Button variant="ghost" onClick={() => { setActiveSettlementEmployee(emp); setShowSettlementModal(true); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-green-700 bg-green-50 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800 h-auto">
                              💰 Abrechnung {stats.hasPaidEntry ? '(Manuell)' : '(Auto)'}
                            </Button>
                            <button onClick={() => { setActiveTargetHoursEmployee(emp); setShowTargetHoursModal(true); }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-slate-300 text-xs font-medium hover:bg-gray-200 dark:hover:bg-zinc-700" title="Soll-Stunden Historie anpassen">
                              <History className="w-3.5 h-3.5" /> Historie
                            </button>
                            <div className="flex items-center gap-2 ml-auto">
                            <Button variant="ghost" size="icon" onClick={() => handleSendReport(emp)} className="text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800 hover:text-accent-mint" title={t('sendReport')}><Mail className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => handlePrintUserReport(emp)} className="text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800" title={t('printReport')}><Printer className="w-4 h-4" /></Button>
                            <div className="relative">
                              <Button variant="ghost" size="icon" onClick={() => setOpenMenuEmpId(openMenuEmpId === emp.id ? null : emp.id)} className="text-gray-400 hover:bg-gray-100 dark:hover:bg-zinc-800" title="Mehr"><MoreVertical className="w-4 h-4" /></Button>
                              {openMenuEmpId === emp.id && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setOpenMenuEmpId(null)} />
                                  <div className="absolute right-0 bottom-full mb-1 z-20 w-48 rounded-lg bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 shadow-lg py-1">
                                    <button onClick={() => { handleToggleAdmin(emp); setOpenMenuEmpId(null); }} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-zinc-700 flex items-center gap-2"><UserCog className="w-4 h-4" /> {t('makeAdmin')}</button>
                                    <button onClick={() => { handleSetDisplayRole(emp, true); setOpenMenuEmpId(null); }} className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-zinc-700 flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Als Anzeige-Gerät</button>
                                    <div className="my-1 border-t border-gray-100 dark:border-zinc-700"></div>
                                    <button onClick={() => { setDeactivatingEmployee(emp); setDeactivateDate(''); setOpenMenuEmpId(null); }} className="w-full text-left px-3 py-2 text-sm text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 flex items-center gap-2"><UserX className="w-4 h-4" /> {t('deactivateEmployee')}</button>
                                    <button onClick={() => { setEmployeeToDelete(emp); setOpenMenuEmpId(null); }} className="w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"><Trash2 className="w-4 h-4" /> {t('delete')}</button>
                                  </div>
                                </>
                              )}
                            </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
                }

                {/* DEACTIVATED EMPLOYEES SECTION */}
                {employees.filter(e => e.role !== 'admin' && e.role !== 'display' && e.approved !== false && isEmployeeDisabled(e)).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-zinc-500 mb-2 flex items-center gap-1.5">
                      <UserX className="w-3.5 h-3.5" />
                      {t('deactivatedEmployees')}
                    </p>
                    <div className="space-y-2">
                      {employees.filter(e => e.role !== 'admin' && e.role !== 'display' && e.approved !== false && isEmployeeDisabled(e)).map(emp => (
                        <div key={emp.id} className="rounded-xl bg-gray-50 dark:bg-zinc-900/40 border border-gray-200 dark:border-zinc-800 opacity-70 px-4 py-3 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm text-gray-500 dark:text-slate-400 truncate">{emp.name}</span>
                              <span className="px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-zinc-700 text-gray-500 dark:text-slate-400 text-[9px] font-bold">{t('deactivatedBadge')}</span>
                            </div>
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {t('deactivatedSince')}: <span className="font-mono">{emp.disabledFrom}</span>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleReactivateEmployee(emp)}
                            className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-1.5 h-auto px-3 py-1.5 text-xs font-bold border border-green-200 dark:border-green-800 rounded-lg flex-shrink-0">
                            <UserCheck className="w-3.5 h-3.5" />
                            {t('reactivateEmployee')}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div >
            )}

            {/* Entries Tab Content */}
            {
              activeAdminTab === 'entries' && (
                <div className="space-y-6">
                  <div className="flex flex-col mb-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                      <h3 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                        <List className="w-5 h-5 mr-2" />
                        {t('entriesAll')}
                      </h3>
                      <Button
                        onClick={() => setEditingEntry({ userId: '', userName: '', date: new Date().toISOString().split('T')[0], startTime: '08:00', endTime: '16:00', breakStartTime: '', breakEndTime: '', type: 'WORK' })}
                        className="flex-1 sm:flex-none w-full sm:w-auto flex items-center justify-center gap-2 bg-accent-mint hover:bg-emerald-400 text-black px-4 py-2 rounded-lg shadow-sm transition-all text-sm font-medium h-auto"
                      >
                        <Plus className="w-4 h-4" />
                        {t('addEntry')}
                      </Button>
                    </div>


                    <div className="flex flex-wrap items-center gap-2 sm:gap-4 border-b border-gray-100 dark:border-slate-700 pb-4 sm:pb-0 sm:border-0">
                      <FilterToggle filterMode={filterMode} setFilterMode={setFilterMode} t={t} />
                      <input
                        type="month"
                        value={adminSelectedMonth}
                        onChange={(e) => setAdminSelectedMonth(e.target.value)}
                        className="text-sm p-1.5 border dark:border-zinc-600 rounded bg-transparent dark:text-zinc-300 outline-none flex-1 sm:flex-none min-w-[120px]"
                        title={t('selectMonth')}
                      />
                      <select
                        value={adminSelectedEmployee}
                        onChange={(e) => setAdminSelectedEmployee(e.target.value)}
                        className="text-sm p-1.5 border dark:border-zinc-600 rounded bg-transparent dark:text-zinc-300 outline-none w-full sm:w-auto sm:max-w-[200px]"
                      >
                        <option value="">{t('allEmployees') || 'Alle Mitarbeiter'}</option>
                        {employees.map(emp => (
                          <option key={emp.id} value={emp.id}>{emp.name}</option>
                        ))}
                      </select>
                      <Button variant="ghost" size="iconSm" onClick={handleAdminEntriesPrint} className="hover:bg-gray-100 rounded text-gray-600 dark:text-slate-400" title={t('print')}>
                        <Printer className="w-5 h-5" />
                      </Button>
                    </div>
                  </div>
                  <div className="hidden print:block mb-8" dir="ltr"><div className="flex justify-between items-end border-b-2 border-black pb-4 mb-4"><img src={LOGO_URL} alt="Logo" className="h-16 object-contain" /><div className="text-right"><h1 className="text-2xl font-bold">{t('printTitle')}</h1></div></div></div>
                  <div className="mb-8 print:mb-0">
                    <div className="overflow-y-auto max-h-[600px] print:max-h-none print:overflow-visible pr-1">
                      <div className="grid grid-cols-4 gap-4 p-4 bg-gray-200 dark:bg-zinc-800 rounded-lg mb-2 font-bold text-xs text-gray-600 dark:text-slate-300 print:text-black print:bg-transparent"><div>{t('employee')}</div><div>{t('dateLabel')}</div><div>{t('time')}</div><div className="text-right">{t('duration')}</div></div>
                      {filteredEntries.map(entry => {
                        const isSick = entry.type === 'SICK';
                        const isVacation = entry.type === 'VACATION';
                        const rowBg = isSick ? 'bg-red-50 dark:bg-red-900/20 border-red-200' : isVacation ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200' : 'bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-700';

                        return (
                          <div key={entry.id} className={`grid grid-cols-4 gap-4 p-4 mb-2 border rounded-lg hover:shadow-md transition-all items-center print:border-b print:mb-0 ${rowBg}`}>
                            <div className="font-semibold text-sm dark:text-slate-200">
                              {entry.userName}
                              {isSick && <span className="ml-2 text-xs bg-red-100 text-red-700 px-1 rounded">Krank</span>}
                              {isVacation && <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-1 rounded">Urlaub</span>}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-slate-400">{new Date(entry.date).toLocaleDateString('de-DE')}</div>
                            <div className="text-sm text-gray-600 dark:text-slate-400">
                              {entry.startTime} - {entry.endTime}
                              {(entry.type !== 'SICK' && entry.type !== 'VACATION') && (
                                (entry.breaks && entry.breaks.length > 0) ? (
                                  entry.breaks.map((b, i) => (
                                    <span key={i} className="text-xs text-accent-mint font-bold ml-2">
                                      {b.start} - {b.end}
                                    </span>
                                  ))
                                ) : (
                                  entry.breakStartTime && entry.breakEndTime ? (
                                    <span className="text-xs text-accent-mint font-bold ml-2">
                                      {entry.breakStartTime} - {entry.breakEndTime}
                                    </span>
                                  ) : (
                                    entry.breakDuration > 0 && <span className="text-xs text-gray-400 ml-2">(Pause: {entry.breakDuration}m)</span>
                                  )
                                )
                              )}
                            </div>
                            <div className="text-right font-mono text-sm flex items-center justify-end gap-2 whitespace-nowrap dark:text-slate-300">
                              {formatHours(entry.duration || 0)}
                              <Button variant="ghost" size="iconSm" onClick={() => setEditingEntry(entry)} className="text-accent-mint hover:text-emerald-400 print:hidden"><Edit2 className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="iconSm" onClick={() => handleDeleteEntry(entry.id)} className="text-red-300 hover:text-red-500 print:hidden"><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        );
                      })}
                      <div className="hidden print:block mt-8 pt-4 border-t-2 border-black break-inside-avoid">
                        <h3 className="font-bold mb-4 uppercase">{t('summary')}</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          {Object.entries(totalsMap).map(([name, val]) => (<React.Fragment key={name}><div className="border-b border-gray-300 py-1">{name}</div><div className="border-b border-gray-300 py-1 text-right font-mono">{formatHours(val)}</div></React.Fragment>))}
                          <div className="font-bold pt-2 mt-2 border-t border-black">{t('total')}</div><div className="font-bold pt-2 mt-2 border-t border-black text-right font-mono">{formatHours(grandTotal)}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )
            }

            {/* Schedule Tab Content */}
            {
              activeAdminTab === 'schedule' && (
                <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-sm border border-slate-200 dark:border-zinc-800 p-4 sm:p-6 overflow-hidden">
                  {/* Merge Shifts with generic Time Entries (Vacation/Sick) for display */}
                  {(() => {
                    const combinedShifts = [
                      ...shifts.map(s => ({ ...s, _collection: 'shifts' })),
                      ...timeEntries.filter(e => (e.type === 'SICK' || e.type === 'VACATION')).map(e => ({ ...e, _collection: 'time_entries' }))
                    ];
                    return (
                      <ShiftScheduleView
                        entries={combinedShifts}
                        employees={staffEmployees}
                        t={t}
                        lang={lang}
                        isAdmin={true}
                        onAddShift={() => setShowShiftModal(true)}
                        onDeleteShift={handleDeleteShift}
                        onEditShift={(shift) => { setEditingShift(shift); setShowShiftModal(true); }}
                      />
                    );
                  })()}
                </div>
              )
            }
          </main >
        </div >
      </MainWrapper >
    );
  }

  // Employee View
  // Filter history list by selected month
  const filteredEntries = timeEntries.filter(e => e.userId === currentProfile.id && e.monthKey === employeeViewMonth);
  // Sort by Date/Time desc
  const sortedFilteredEntries = sortEntries(filteredEntries);

  // ADMIN CONSOLE
  if (currentProfile.role === 'admin') {
    return (
      <MainWrapper isRTL={isRTL}>
        <Toast notification={notification} onClose={() => setNotification(null)} />
        <div className="pb-20">
          <AppHeader title={t('adminConsole')} subtitle={currentProfile.name} icon={Shield} colorClass="purple" isRTL={isRTL} lang={lang} setLang={setLang} showPasswordChange={showPasswordChange} setShowPasswordChange={setShowPasswordChange} newPassword={newPassword} setNewPassword={setNewPassword} handleChangePassword={handleChangePassword} onLogout={handleLogout} t={t} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          <main className="max-w-4xl mx-auto p-4 space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl shadow border border-purple-100 dark:border-purple-900">
                <div className="text-gray-500 dark:text-slate-400 text-sm font-medium mb-1">{t('employee')}</div>
                <div className="text-3xl font-bold text-accent-lilac dark:text-accent-lilac">{employees.length}</div>
              </div>
              <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl shadow border border-blue-100 dark:border-blue-900">
                <div className="text-gray-500 dark:text-slate-400 text-sm font-medium mb-1">{t('entriesAll')}</div>
                <div className="text-3xl font-bold text-accent-mint dark:text-accent-mint">{timeEntries.length}</div>
              </div>
              <div className="bg-white dark:bg-zinc-900 p-6 rounded-xl shadow border border-green-100 dark:border-green-900">
                <div className="text-gray-500 dark:text-slate-400 text-sm font-medium mb-1">{t('total')} (Std)</div>
                <div className="text-3xl font-bold text-green-600 dark:text-accent-mint">{formatHours(timeEntries.reduce((s, e) => s + (Number(e.duration) || 0), 0))}</div>
              </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="bg-white dark:bg-zinc-900 rounded-xl shadow p-6 border border-gray-100 dark:border-zinc-800">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white">{t('employeeOverview')}</h3>
                <div className="flex space-x-2">
                  <Button variant="ghost" size="sm" onClick={() => setFilterMode('month')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors h-auto ${filterMode === 'month' ? 'bg-accent-mint text-black shadow-md' : 'bg-white dark:bg-zinc-800 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-zinc-700'}`}>{t('viewMonth')}</Button>
                  <Button variant="ghost" size="sm" onClick={() => setFilterMode('all')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors h-auto ${filterMode === 'all' ? 'bg-accent-mint text-black shadow-md' : 'bg-white dark:bg-zinc-800 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-zinc-700'}`}>{t('viewAll')}</Button>
                </div>
              </div>

              {/* PENDING APPROVALS */}
              {employees.some(e => e.approved === false) && (
                <div className="mb-8">
                  <h3 className="text-xl font-bold text-accent-coral dark:text-accent-coral mb-4 flex items-center"><AlertTriangle className="w-6 h-6 mr-2" /> {t('pendingApprovals')}</h3>
                  <div className="grid gap-4">
                    {employees.filter(e => e.approved === false).map(emp => (
                      <div key={emp.id} className="bg-orange-50 dark:bg-accent-coral/20 rounded-xl p-4 border border-orange-200 dark:border-accent-coral/50 flex justify-between items-center">
                        <div>
                          <h4 className="font-bold text-gray-800 dark:text-white">{emp.name}</h4>
                          <p className="text-sm text-gray-600 dark:text-slate-400">@{emp.username}</p>
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="primary" size="sm" onClick={() => handleApproveUser(emp)} className="bg-green-500 hover:bg-green-600 px-3 py-1 text-sm font-bold shadow h-auto">{t('approve')}</Button>
                          <Button variant="destructive" size="sm" onClick={() => handleRejectUser(emp)} className="bg-red-500 hover:bg-red-600 px-3 py-1 text-sm font-bold shadow h-auto">{t('reject')}</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ADMIN LIST */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-accent-lilac dark:text-accent-lilac mb-4 flex items-center"><Shield className="w-5 h-5 mr-2" /> Admins</h3>
                <div className="grid gap-4">
                  {employees.filter(e => e.role === 'admin').map(emp => (
                    <div key={emp.id} className="bg-purple-50 dark:bg-accent-lilac/20 rounded-xl p-4 border border-purple-200 dark:border-accent-lilac/50">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-bold text-gray-800 dark:text-white flex items-center">
                            {editingNameId === emp.id ? (
                              <div className="flex items-center space-x-2">
                                <input type="text" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-24" placeholder={t('firstNameLabel')} autoFocus />
                                <input type="text" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-24" placeholder={t('lastNameLabel')} />
                                <input type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-24" placeholder={t('usernameLabel')} />
                                <Button variant="ghost" size="iconSm" onClick={() => handleUpdateName(emp.id)} className="text-green-600 hover:text-green-800"><Save className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="iconSm" onClick={() => setEditingNameId(null)} className="text-red-600 hover:text-red-800"><X className="w-4 h-4" /></Button>
                              </div>
                            ) : (
                              <>
                                <span className="mr-1">{emp.firstName || emp.name.split(' ')[0]}</span>
                                <span className="font-normal text-gray-600 dark:text-slate-400 mr-2">{emp.lastName || emp.name.split(' ').slice(1).join(' ')}</span>
                                <span className="text-xs text-gray-400 mr-2">({emp.username || '-'})</span>
                                <Button variant="ghost" size="iconSm" onClick={() => { setEditingNameId(emp.id); setNewFirstName(emp.firstName || emp.name.split(' ')[0]); setNewLastName(emp.lastName || emp.name.split(' ').slice(1).join(' ')); setNewUsername(emp.username || ''); }} className="text-gray-400 hover:text-gray-600"><Edit2 className="w-3 h-3" /></Button>
                              </>
                            )}
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold border border-purple-200">{t('adminBadge')}</span>
                          </h4>
                          <div className="text-xs text-gray-400 mt-1 flex items-center"><Key className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} /> <button type="button" onClick={() => handleAdminResetPin(emp)} className="underline hover:text-accent-mint">PIN zurücksetzen</button></div>
                        </div>
                        <div className="flex space-x-2">
                          <Button variant="ghost" size="icon" onClick={() => handleToggleAdmin(emp)} className="hover:bg-white/50 text-purple-600" title={t('removeAdmin')}><UserCog className="w-5 h-5" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* EMPLOYEE LIST */}
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-gray-800 dark:text-white pt-4 border-t dark:border-slate-700">{t('employeeOverview')}</h3>
                {employees.filter(e => e.role !== 'admin' && e.role !== 'display' && e.approved !== false && !isEmployeeDisabled(e)).map(emp => {
                  const stats = getEmployeeStats(emp.id);
                  const percentage = (stats.total / (stats.paidOrDue || 1)) * 100;
                  const isAdmin = emp.role === 'admin';
                  return (
                    <div key={emp.id} className={`bg-white dark:bg-zinc-900 rounded-xl shadow p-5 border-l-4 ${isRTL ? 'border-r-4 border-l-0' : ''} ${percentage > 80 ? 'border-red-400' : 'border-green-400'}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-bold text-lg dark:text-white flex items-center">
                            {editingNameId === emp.id ? (
                              <div className="flex items-center space-x-2">
                                <input type="text" value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-24" placeholder={t('firstNameLabel')} autoFocus />
                                <input type="text" value={newLastName} onChange={(e) => setNewLastName(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-24" placeholder={t('lastNameLabel')} />
                                <input type="text" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} className="border rounded px-2 py-1 text-sm text-black w-24" placeholder={t('usernameLabel')} />
                                <Button variant="ghost" size="iconSm" onClick={() => handleUpdateName(emp.id)} className="text-green-600 hover:text-green-800"><Save className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="iconSm" onClick={() => setEditingNameId(null)} className="text-red-600 hover:text-red-800"><X className="w-4 h-4" /></Button>
                              </div>
                            ) : (
                              <>
                                <span className="mr-1">{emp.firstName || emp.name.split(' ')[0]}</span>
                                <span className="font-normal text-gray-600 dark:text-slate-400 mr-2">{emp.lastName || emp.name.split(' ').slice(1).join(' ')}</span>
                                <span className="text-xs text-gray-400 mr-2">({emp.username || '-'})</span>
                                <Button variant="ghost" size="iconSm" onClick={() => { setEditingNameId(emp.id); setNewFirstName(emp.firstName || emp.name.split(' ')[0]); setNewLastName(emp.lastName || emp.name.split(' ').slice(1).join(' ')); setNewUsername(emp.username || ''); }} className="text-gray-400 hover:text-gray-600"><Edit2 className="w-3 h-3" /></Button>
                              </>
                            )}
                            {isAdmin && <span className="ml-2 px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold border border-purple-200">{t('adminBadge')}</span>}
                          </h4>
                          <div className="text-xs text-gray-400 mt-1 flex items-center"><Key className={`w-3 h-3 ${isRTL ? 'ml-1' : 'mr-1'}`} /> <button type="button" onClick={() => handleAdminResetPin(emp)} className="underline hover:text-accent-mint">PIN zurücksetzen</button></div>
                        </div>
                        <div className="flex space-x-2">

                          <Button variant="ghost" size="icon" onClick={() => handlePrintUserReport(emp)} className="hover:bg-gray-100 dark:hover:bg-gray-700 text-accent-mint" title={t('print')}><Printer className="w-5 h-5" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleSendReport(emp)} className="hover:bg-gray-100 dark:hover:bg-gray-700 text-yellow-600" title={t('sendReport')}><Mail className="w-5 h-5" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => handleToggleAdmin(emp)} className={`hover:bg-gray-100 dark:hover:bg-gray-700 ${isAdmin ? 'text-purple-600' : 'text-gray-400'}`} title={isAdmin ? t('removeAdmin') : t('makeAdmin')}><UserCog className="w-5 h-5" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => { setDeactivatingEmployee(emp); setDeactivateDate(''); }} className="hover:bg-orange-50 dark:hover:bg-orange-900/20 text-orange-500" title={t('deactivateEmployee')}><UserX className="w-5 h-5" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => setEmployeeToDelete(emp)} className="hover:bg-red-50 dark:hover:bg-red-900/30 text-red-400" title={t('delete')}><Trash2 className="w-5 h-5" /></Button>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center space-x-4 text-sm">
                        <div className="flex items-center text-gray-600 dark:text-slate-400"><Clock className="w-4 h-4 mr-1" /> {formatHours(stats.total)} / {formatHours(stats.paidOrDue)} Std</div>
                        <div className="flex-1 h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden"><div className={`h-full rounded-full ${percentage > 100 ? 'bg-red-500' : percentage > 80 ? 'bg-yellow-500' : 'bg-green-500'}`} style={{ width: `${Math.min(percentage, 100)}%` }}></div></div>
                        <div className="flex flex-col gap-1 items-end">
                          <div className="flex items-center"><input type="number" value={emp.maxHours} onChange={(e) => handleUpdateMaxHours(emp.id, e.target.value)} className="w-12 sm:w-16 p-1 border rounded text-right text-xs dark:bg-slate-700 dark:text-white dark:border-slate-600" /> <span className="ml-1 text-gray-500 text-xs w-8">Max</span></div>
                          <div className="flex items-center"><input type="number" value={emp.weeklyHours || 0} onChange={(e) => handleUpdateWeeklyHours(emp.id, e.target.value)} className="w-12 sm:w-16 p-1 border rounded text-right text-xs dark:bg-slate-700 dark:text-white dark:border-slate-600" /> <span className="ml-1 text-gray-500 text-[10px] sm:text-xs w-8">W/Std</span></div>
                        </div>
                      </div>
                      <div className="mt-2 flex gap-2">
                        <div className="flex-1">
                          <input type="email" value={emp.email || ''} onChange={(e) => handleUpdateEmail(emp.id, e.target.value)} placeholder={t('emailLabel')} className="w-full p-1 border rounded text-xs dark:bg-slate-700 dark:text-white dark:border-slate-600" />
                        </div>
                        <div className="flex items-center gap-1">
                          <input type="color" value={emp.color || '#3b82f6'} onChange={(e) => handleUpdateColor(emp.id, e.target.value)} className="w-8 h-6 p-0 border-0 rounded cursor-pointer" title="Mitarbeiter Farbe" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* DEACTIVATED EMPLOYEES SECTION */}
              {employees.filter(e => e.role !== 'admin' && e.role !== 'display' && e.approved !== false && isEmployeeDisabled(e)).length > 0 && (
                <div className="space-y-3 mt-4 pt-4 border-t dark:border-slate-700">
                  <h3 className="text-base font-bold text-gray-400 dark:text-slate-500 flex items-center gap-2">
                    <UserX className="w-4 h-4" />
                    {t('deactivatedEmployees')}
                  </h3>
                  {employees.filter(e => e.role !== 'admin' && e.role !== 'display' && e.approved !== false && isEmployeeDisabled(e)).map(emp => (
                    <div key={emp.id} className="bg-gray-50 dark:bg-zinc-800/40 rounded-xl p-4 border-l-4 border-gray-300 dark:border-zinc-600 opacity-60">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm text-gray-500 dark:text-slate-400">{emp.name}</span>
                            <span className="px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-zinc-700 text-gray-500 dark:text-slate-400 text-[9px] font-bold">{t('deactivatedBadge')}</span>
                          </div>
                          <div className="text-[10px] text-gray-400 mt-0.5">
                            {t('deactivatedSince')}: <span className="font-mono">{emp.disabledFrom}</span>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleReactivateEmployee(emp)}
                          className="text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 flex items-center gap-1.5 h-auto px-3 py-1.5 text-xs font-bold border border-green-200 dark:border-green-800 rounded-lg">
                          <UserCheck className="w-3.5 h-3.5" />
                          {t('reactivateEmployee')}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </main>
        </div>
      </MainWrapper>
    );
  }



  // EMPLOYEE DASHBOARD
  return (
    <MainWrapper isRTL={isRTL}>
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <div className="pb-20">
        <AppHeader title={currentProfile.name} subtitle={t('employeeDashboard')} icon={User} colorClass="blue" isRTL={isRTL} lang={lang} setLang={setLang} showPasswordChange={showPasswordChange} setShowPasswordChange={setShowPasswordChange} newPassword={newPassword} setNewPassword={setNewPassword} handleChangePassword={handleChangePassword} onLogout={handleLogout} t={t} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
        <EditModal editingEntry={editingEntry} setEditingEntry={setEditingEntry} handleUpdateEntry={handleUpdateEntry} t={t} isRTL={isRTL} employees={staffEmployees} />
        <main className="max-w-2xl mx-auto p-4 space-y-6">
          {/* Tab Navigation */}
          <div className="flex gap-2 border-b dark:border-slate-700">
            <Button
              variant={activeEmployeeTab === 'timeTracking' ? 'ghost' : 'ghost'}
              onClick={() => setActiveEmployeeTab('timeTracking')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 h-auto rounded-none ${activeEmployeeTab === 'timeTracking'
                ? 'border-accent-mint text-accent-mint dark:text-accent-mint bg-transparent'
                : 'border-transparent text-gray-600 dark:text-slate-400 hover:text-accent-mint bg-transparent'
                }`}
            >
              <Clock className="w-4 h-4 inline mr-2" />
              {t('recordTime')}
            </Button>
            <Button
              variant={activeEmployeeTab === 'myShifts' ? 'ghost' : 'ghost'}
              onClick={() => setActiveEmployeeTab('myShifts')}
              className={`px-4 py-2 font-medium transition-colors border-b-2 h-auto rounded-none ${activeEmployeeTab === 'myShifts'
                ? 'border-accent-mint text-accent-mint dark:text-accent-mint bg-transparent'
                : 'border-transparent text-gray-600 dark:text-slate-400 hover:text-accent-mint bg-transparent'
                }`}
            >
              <CalendarDays className="w-4 h-4 inline mr-2" />
              {t('myShiftsTab')}
            </Button>
          </div>

          {/* Time Tracking Tab */}
          {activeEmployeeTab === 'timeTracking' && (
            <>
              {(() => {
                const stats = getEmployeeStats(currentProfile.id, employeeViewMonth);
                const percentage = (stats.total / (stats.paidOrDue || 1)) * 100;

                const getStatusUI = () => {
                  if (percentage >= 100) return { icon: <AlertTriangle className="w-5 h-5 mr-2" />, color: "text-red-600", text: t('limitExceeded') };
                  if (percentage > 80) return { icon: <AlertTriangle className="w-5 h-5 mr-2" />, color: "text-yellow-600", text: t('limitReached') };
                  if (percentage > 0) return { icon: <CheckCircle className="w-5 h-5 mr-2" />, color: "text-accent-mint", text: t('allGood') };
                  return { icon: <Info className="w-5 h-5 mr-2" />, color: "text-accent-mint", text: t('recordTimeDesc') || 'Erfasse deine Zeiten.' };
                };
                const statusUI = getStatusUI();

                return (
                  <div className="bg-white dark:bg-zinc-900 rounded-xl shadow p-6 border border-gray-100 dark:border-zinc-800">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-semibold dark:text-white uppercase tracking-wider text-xs text-gray-400">{t('monthlyStatus')}</h3>
                      <input
                        type="month"
                        value={employeeViewMonth}
                        onChange={(e) => setEmployeeViewMonth(e.target.value)}
                        className="text-sm p-1 border dark:border-slate-600 rounded bg-transparent dark:text-slate-300 outline-none"
                        title={t('selectMonth')}
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="text-center">
                        <div className="text-[10px] text-gray-500 uppercase mb-1">Übertrag</div>
                        <div className={`font-bold text-lg ${stats.carryOver > 0 ? 'text-accent-mint' : 'text-gray-400'}`}>
                          {formatHours(stats.carryOver)}
                        </div>
                      </div>
                      <div className="text-center border-x dark:border-slate-700">
                        <div className="text-[10px] text-gray-500 uppercase mb-1">Ist-Stunden</div>
                        <div className="font-bold text-lg dark:text-white">
                          {formatHours(stats.worked)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-[10px] text-gray-500 uppercase mb-1">Gesamt</div>
                        <div className={`font-bold text-lg ${percentage > 100 ? 'text-red-600' : 'text-accent-mint'}`}>
                          {formatHours(stats.total)}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-between mb-2 text-xs text-gray-500 dark:text-slate-400">
                      <span>{t('stand')}: {Math.round(percentage)}%</span>
                      <span>Limit: {formatHours(stats.paidOrDue)}</span>
                    </div>
                    <ProgressBar current={stats.total} max={stats.paidOrDue} />

                    <div className={`mt-6 p-4 rounded-xl bg-gray-50 dark:bg-zinc-800/50 flex items-center font-medium ${statusUI.color}`}>
                      {statusUI.icon}
                      <span className="text-sm">{statusUI.text}</span>
                    </div>
                  </div>
                );
              })()}

              <div className="bg-white dark:bg-zinc-900 rounded-xl shadow p-6 border border-gray-100 dark:border-zinc-800">
                <h3 className="font-semibold mb-4 dark:text-white">{t('recordTime')}</h3>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  {/* Type Selector */}
                  <div className="col-span-3 mb-2">
                    <label className="text-xs font-medium text-gray-500 dark:text-slate-400 block mb-1">{t('type') || 'Typ'}</label>
                    <div className="grid grid-cols-3 gap-2">
                      <label className={`flex items-center justify-center p-2 rounded cursor-pointer border ${entryType === 'WORK' ? 'bg-accent-mint/10 border-accent-mint text-accent-mint dark:bg-accent-mint/20 dark:text-accent-mint' : 'border-gray-200 dark:border-zinc-700 dark:text-slate-300'}`}>
                        <input type="radio" name="entryType" value="WORK" checked={entryType === 'WORK'} onChange={() => setEntryType('WORK')} className="hidden" />
                        <span className="mr-1">🏢</span> <span className="truncate">{t('work') || 'Arbeit'}</span>
                      </label>
                      <label className={`flex items-center justify-center p-2 rounded cursor-pointer border ${entryType === 'VACATION' ? 'bg-yellow-50 border-yellow-500 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-300' : 'border-gray-200 dark:border-slate-700 dark:text-slate-300'}`}>
                        <input type="radio" name="entryType" value="VACATION" checked={entryType === 'VACATION'} onChange={() => setEntryType('VACATION')} className="hidden" />
                        <span className="mr-1">🏖️</span> <span className="truncate">{t('vacation') || 'Urlaub'}</span>
                      </label>
                      <label className={`flex items-center justify-center p-2 rounded cursor-pointer border ${entryType === 'SICK' ? 'bg-red-50 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-300' : 'border-gray-200 dark:border-slate-700 dark:text-slate-300'}`}>
                        <input type="radio" name="entryType" value="SICK" checked={entryType === 'SICK'} onChange={() => setEntryType('SICK')} className="hidden" />
                        <span className="mr-1">🤒</span> <span className="truncate">{t('krankheit') || 'Krankheit'}</span>
                      </label>
                    </div>
                  </div>

                  {/* Date Input: Range for Leave, Single for Work */}
                  {/* Conditional Rendering based on Type */}
                  {entryType === 'WORK' ? (
                    <>
                      {/* Date Input for Work */}
                      <div className="col-span-3 lg:col-span-1">
                        <label className="text-xs block mb-1 dark:text-slate-400">{t('dateLabel')}</label>
                        <input dir="ltr" type="date" value={entryDate} onChange={handleEntryDateChange} className="w-full p-2 border dark:border-slate-600 rounded text-left bg-white dark:bg-slate-700 dark:text-white" />
                      </div>

                      {/* Start/End Time for Work */}
                      <div className="col-span-1 border-l-0 lg:border-l pl-0 lg:pl-4">
                        <label className="text-xs block mb-1 dark:text-slate-400">{t('startLabel')}</label>
                        <TimeSelect value={startTime} onChange={(val) => setStartTime(val)} />
                      </div>
                      <div className="col-span-1">
                        <label className="text-xs block mb-1 dark:text-slate-400">{t('endLabel')}</label>
                        <TimeSelect value={endTime} onChange={(val) => setEndTime(val)} />
                      </div>

                      {/* Breaks for Work */}
                      <div className="col-span-3">
                        <label className="text-xs font-medium text-gray-500 dark:text-slate-400 block mb-2">{t('breaks') || 'Pausen'}</label>
                        {breaks.map((b, i) => (
                          <div key={i} className="flex gap-2 mb-2 items-center">
                            <div className="flex-1">
                              <TimeSelect value={b.start} onChange={(val) => {
                                const newBreaks = [...breaks];
                                newBreaks[i].start = val;
                                setBreaks(newBreaks);
                              }} />
                            </div>
                            <span className="text-gray-400 self-center">-</span>
                            <div className="flex-1">
                              <TimeSelect value={b.end} onChange={(val) => {
                                const newBreaks = [...breaks];
                                newBreaks[i].end = val;
                                setBreaks(newBreaks);
                              }} />
                            </div>
                            <Button variant="ghost" size="iconSm" onClick={() => {
                              const newBreaks = [...breaks];
                              newBreaks.splice(i, 1);
                              setBreaks(newBreaks);
                            }} className="text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        ))}
                        <Button variant="ghost" size="sm" onClick={() => setBreaks([...breaks, { start: '', end: '' }])} className="text-sm text-accent-mint hover:underline flex items-center mt-1 h-auto px-0 hover:bg-transparent">
                          <Plus className="w-3 h-3 mr-1" /> {t('addBreak') || 'Pause hinzufügen'}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Duration Selection (Now on Top) */}
                      <div className="col-span-3">
                        <label className="text-xs block mb-1 dark:text-slate-400">{t('duration') || 'Dauer'}</label>
                        <div className="grid grid-cols-2 gap-2">
                          <label className={`flex items-center justify-center p-2 rounded cursor-pointer border ${leaveDuration === 'FULL' ? 'bg-accent-mint/10 border-accent-mint text-accent-mint dark:bg-accent-mint/20 dark:text-accent-mint' : 'border-gray-200 dark:border-zinc-700 dark:text-slate-300'}`}>
                            <input type="radio" value="FULL" checked={leaveDuration === 'FULL'} onChange={() => setLeaveDuration('FULL')} className="hidden" />
                            <span className="mr-1">🌕</span> <span className="truncate">{t('fullDay') || 'Ganzer Tag'}</span>
                          </label>
                          <label className={`flex items-center justify-center p-2 rounded cursor-pointer border ${leaveDuration === 'HALF' ? 'bg-accent-mint/10 border-accent-mint text-accent-mint dark:bg-accent-mint/20 dark:text-accent-mint' : 'border-gray-200 dark:border-zinc-700 dark:text-slate-300'}`}>
                            <input type="radio" value="HALF" checked={leaveDuration === 'HALF'} onChange={() => setLeaveDuration('HALF')} className="hidden" />
                            <span className="mr-1">🌗</span> <span className="truncate">{t('halfDay') || 'Halber Tag'}</span>
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          {currentProfile.employmentType === 'MINI_JOB' || currentProfile.employmentType === 'MIDI_JOB'
                            ? 'Dauer wird anhand von Dienstplan bzw. 13-Wochen-Schnitt berechnet.'
                            : leaveDuration === 'FULL' ? '08:00 - 16:00 (8h)' : '08:00 - 12:00 (4h)'}
                        </p>
                      </div>

                      {/* Date Range Selection (Now Below) */}
                      <div className="col-span-3 grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs block mb-1 dark:text-slate-400">{t('dateFrom') || 'Von'}</label>
                          <input dir="ltr" type="date" value={entryDate} onChange={handleEntryDateChange} className="w-full p-2 border dark:border-slate-600 rounded text-left bg-white dark:bg-slate-700 dark:text-white" />
                        </div>
                        <div>
                          <label className="text-xs block mb-1 dark:text-slate-400">{t('dateTo') || 'Bis'}</label>
                          <input dir="ltr" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={entryDate} className="w-full p-2 border dark:border-slate-600 rounded text-left bg-white dark:bg-slate-700 dark:text-white" />
                        </div>
                      </div>
                    </>
                  )}

                </div>
                <Button onClick={handleAddEntry} disabled={isSubmitting} className="w-full bg-accent-mint text-black py-3 rounded font-bold hover:bg-emerald-400 h-auto justify-center"><Plus className={`w-4 h-4 inline ${isRTL ? 'ml-1' : 'mr-1'}`} /> {t('saveButton')}</Button>
              </div>
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold dark:text-white">{t('history')} ({employeeViewMonth})</h3>
                  <div className="flex space-x-2 print:hidden">
                    <Button variant="ghost" size="sm" onClick={handleEmployeePrint} className="flex items-center bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-slate-300 px-3 py-2 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-zinc-700 h-auto">
                      <Printer className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('printReport')}
                    </Button>
                  </div>
                </div>
                {/* FilterToggle removed here because we filter by the selected month above */}
                <div className="space-y-3">
                  {sortedFilteredEntries.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Keine Einträge für diesen Monat.</p>}
                  {sortedFilteredEntries.map(entry => (
                    <div key={entry.id} className={`p-4 rounded shadow border flex justify-between items-center ${entry.type === 'SICK' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-900/50' :
                      entry.type === 'VACATION' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-900/50' :
                        'bg-white border-gray-100 dark:bg-zinc-900 dark:border-zinc-800'
                      }`}>
                      <div>
                        <div className="font-bold text-sm dark:text-white">{new Date(entry.date).toLocaleDateString('de-DE')}</div>
                        <div className="text-xs text-gray-500 dark:text-slate-400">
                          {entry.type === 'SICK' ? (
                            <span className="font-bold text-red-600 dark:text-red-400 flex items-center gap-1">
                              🤒 {t('sick') || 'Krankheit'}
                              <span className="text-gray-400 font-normal ml-1">
                                ({entry.duration >= 8 ? (t('fullDay') || 'Ganzer Tag') : (t('halfDay') || 'Halber Tag')})
                              </span>
                            </span>
                          ) : entry.type === 'VACATION' ? (
                            <span className="font-bold text-yellow-600 dark:text-yellow-400 flex items-center gap-1">
                              🏖️ {t('vacation') || 'Urlaub'}
                              <span className="text-gray-400 font-normal ml-1">
                                ({entry.duration >= 8 ? (t('fullDay') || 'Ganzer Tag') : (t('halfDay') || 'Halber Tag')})
                              </span>
                            </span>
                          ) : (
                            <>
                              {entry.startTime} - {entry.endTime}
                              {(entry.breaks && entry.breaks.length > 0) ? (
                                entry.breaks.map((b, i) => (
                                  <span key={i} className="text-accent-mint font-bold ml-2">
                                    {b.start} - {b.end}
                                  </span>
                                ))
                              ) : (
                                entry.breakStartTime && entry.breakEndTime ? (
                                  <span className="text-accent-mint font-bold ml-2">
                                    {entry.breakStartTime} - {entry.breakEndTime}
                                  </span>
                                ) : (
                                  entry.breakDuration > 0 && <span className="ml-1 text-gray-400">(Pause: {entry.breakDuration}m)</span>
                                )
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`font-bold px-2 py-1 rounded text-xs whitespace-nowrap ${entry.type === 'SICK' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                          entry.type === 'VACATION' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' :
                            'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-slate-200'
                          }`}>{formatHours(entry.duration)}</span>
                        <Button variant="ghost" size="iconSm" onClick={() => setEditingEntry(entry)} className="text-accent-mint hover:text-emerald-400"><Edit2 className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="iconSm" onClick={() => handleDeleteEntry(entry.id)} className="text-red-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* My Shifts Tab */}
          {
            activeEmployeeTab === 'myShifts' && (
              <div className="space-y-4">
                <div className="bg-white dark:bg-zinc-900 rounded-xl shadow p-6 border border-gray-100 dark:border-zinc-800">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-white flex items-center gap-2">
                      <Calendar className="w-5 h-5" /> {t('upcomingShifts') || 'Kommende Schichten'}
                    </h3>
                  </div>
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    const upcomingShifts = shifts
                      .filter(s => s.employeeId === currentProfile.id)
                      .filter(s => {
                        const shiftDate = new Date(s.date);
                        return shiftDate >= today;
                      })
                      .sort((a, b) => new Date(a.date) - new Date(b.date));

                    if (upcomingShifts.length === 0) {
                      return (
                        <div className="text-center py-8 text-gray-500 dark:text-slate-400">
                          <CalendarDays className="w-12 h-12 mx-auto mb-2 opacity-50" />
                          <p>{t('noShiftsScheduled')}</p>
                        </div>
                      );
                    }

                    return upcomingShifts.map(shift => {
                      const shiftDate = new Date(shift.date);
                      const isToday = shiftDate.toDateString() === today.toDateString();
                      const dayName = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][shiftDate.getDay()];

                      return (
                        <div
                          key={shift.id}
                          className={`p-4 rounded-lg border-l-4 mb-3 ${isToday
                            ? 'bg-accent-mint/10 dark:bg-accent-mint/20 border-accent-mint'
                            : 'bg-gray-50 dark:bg-zinc-800 border-gray-300 dark:border-zinc-700'
                            }`}
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              {isToday && (
                                <span className="inline-block px-2 py-0.5 bg-accent-mint text-black text-xs font-bold rounded mb-1">
                                  {t('today').toUpperCase()}
                                </span>
                              )}
                              <div className="font-bold text-gray-800 dark:text-white">
                                {t(dayName)}, {shiftDate.getDate()}.{shiftDate.getMonth() + 1}.{shiftDate.getFullYear()}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-slate-300 mt-1">
                                {shift.startTime} - {shift.endTime}
                              </div>
                              {shift.shiftType && (
                                <div className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                                  {shift.shiftType}
                                </div>
                              )}
                              {shift.notes && (
                                <div className="text-xs text-gray-500 dark:text-slate-400 mt-2 italic">
                                  {shift.notes}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                {/* Shift Modal for Employees */}
                <ShiftModal
                  show={showShiftModal}
                  onClose={() => { setShowShiftModal(false); setEditingShift(null); }}
                  editingShift={editingShift}
                  employees={[currentProfile]}
                  onSave={handleShiftModalSave}
                  t={t}
                  isRTL={isRTL}
                />
              </div>
            )
          }
        </main >
      </div >
    </MainWrapper >
  );
}